/**
 * VibeLiveCore.js
 * 
 * A simplified wrapper around Bridge.js for building video chat UIs.
 * Manages all Bridge interactions, media state, and member tracking.
 * 
 * Usage:
 *   const chat = new VibeLiveCore();
 *   chat.onLogin = (user) => { ... };
 *   chat.onRemoteMemberStreamStart = (memberId, streamType) => { ... };
 *   await chat.login(email, password, contextId);
 * 
 * For anonymous users:
 *   const chat = new VibeLiveCore({ contextId: 'xxx' });                // trial
 *   const chat = new VibeLiveCore({ contextId: 'xxx', proxy: '/my-proxy' }); // production
 *   await chat.signupAnonymous('MyName');
 *   const channel = await chat.createChannel({ title: 'My Room' });
 *   await chat.enterByRoomCode(channel.room_code);  // Enter PRE-LIVE
 *   await chat.startLive();  // Go LIVE
 */

import Bridge from './bridge.js';
import Fetch from './fetch.js';
import MakedoConfig from './_config.js';
import * as BrowserActivity from './bactivity.js';
import { createMonitor as createQualityMonitor } from './connection-quality.js';
import { MakedoError, ErrorCodes } from './errors.js';

export default class VibeLiveCore {
    // ========== INTERNAL STATE ==========
    #bridge = null;
    // App-set default downstream subscription map (init({ defaultSubscription }) or setDefaultSubscription()).
    // Held here because the bridge is recreated per session; re-applied in #setupGlobalCallbacks().
    #defaultSubscription = null;
    // When false, the SDK does NOT fetch per-member data (getOneMember) in response to presence
    // events — it builds minimal member entries instead. This removes the O(N²) getOneMember storm
    // in large rooms (every member fetching every other member on join). Opt-in via
    // init({ trackMembers: false }) for clients that don't render the member roster (e.g. a mosaic
    // compositor that seats by memberId, or publish-only "leaf" members). Default true (unchanged).
    #trackMembers = true;
    #currentUser = null;
    #localMemberId = null;
    #currentChannelId = null;
    #contextId = null;
    #proxyUrl = null;
    #projectKey = null;
    #channels = [];
    #users = [];
    #selectedChannel = null;
    #inChannel = false;
    #requestedDisplayName = null;
    
    #localMedia = {
        audio: false,
        video: false,
        audioMuted: false,
        videoMuted: false
    };
    
    #screenshare = {
        audio: false,
        video: false,
        videoMuted: false
    };
    
    #members = new Map(); // member_id -> { displayName, baseStatus, hasCamera, hasScreenshare, videoElements: {} }
    #pendingMembers = new Set();
    
    // DOM elements - core manages srcObject only, visibility is app's responsibility
    #localVideoElement = null;
    #localScreenElement = null;

    // Tile registry: memberId -> { camera: Element|null, screenshare: Element|null }
    // When registered, the SDK auto-removes tiles from DOM on lifecycle events.
    #tileRegistry = new Map();

    // When false, #removeTile clears srcObject but skips element.remove().
    // Set via init({ tileAutoRemove: false }). Default true.
    #tileAutoRemove = true;

    // When true (default), the SDK hides the <video> element it owns inside a
    // registered tile whenever that stream's video is not 'ON' (OFF or MUTED).
    // This prevents the WebRTC "frozen last frame" trap: a remote camera that
    // turns off keeps its MediaStream alive (audio continues), so the <video>
    // would otherwise freeze on the last decoded frame indefinitely. The SDK
    // only toggles a `.vibelive-video-off` class on the <video> it attached the
    // stream to (display:none via an injected rule) — the surrounding tile chrome
    // (placeholder, dim, avatar) remains the app's responsibility. Audio is never
    // touched. Set init({ autoHideStoppedVideo: false }) to opt out and render the
    // frozen frame yourself.
    #autoHideStoppedVideo = true;

    // Optional feature flags set at construction. Currently:
    //   features.connectionQuality: boolean — when true, start the local quality monitor
    //     on channel entry and emit onConnectionQuality / onMemberQuality events.
    //   features.showMemberQuality: boolean — when true (implies connectionQuality),
    //     auto-render a top-edge status bar on remote camera tiles when their quality
    //     drops below 'good'.
    //   features.showLocalQuality: boolean — when true (implies connectionQuality),
    //     auto-render the same status bar on the local camera tile based on the overall
    //     (your-side) snapshot.
    //   features.qualityThresholds: object — optional override of the default RTT /
    //     packet-loss / jitter / stall thresholds. Deep-merged one level over the
    //     SDK defaults; e.g. { rttMs: { fair: 250 } } only changes that single value.
    #features = { connectionQuality: false, showMemberQuality: false, showLocalQuality: false };
    // Optional auto-match defaults from init({ autoMatch: { role, attributes } }).
    // Merged into every setAvailable() call so apps can declare their role/tags once.
    #autoMatchDefaults = null;

    // User-supplied threshold overrides for the quality monitor (passed through to
    // createMonitor when #startQualityMonitor runs). null = use SDK defaults.
    #qualityThresholds = null;

    // Active quality monitor instance (only when feature flag is on AND in a channel).
    #qualityMonitor = null;
    // Bound offline/online handlers — stored so removeEventListener can target them exactly.
    #qualityOfflineHandler = null;
    #qualityOnlineHandler  = null;
    
    // ========== CONSTRUCTOR ==========
    
    /**
     * Create a new VibeLiveCore instance.
     * @param {Object} [config] - Optional configuration
     * @param {string} [config.serverUrl] - Server base URL (default: https://makedo.com)
     * @param {string} [config.contextId] - Your app's context ID (required for anonymous signups)
     * @param {string} [config.projectId] - Alias for contextId
     * @param {string} [config.proxy] - Proxy URL for production projects (adds auth token server-side)
     * @param {string} [config.projectKey] - Project auth key for non-proxy authorization (sent as X-Project-Auth-Token header)
     * @param {boolean} [config.tileAutoRemove=true] - When false, registered tiles are kept in the DOM on member exit (clear srcObject only) so apps can animate a grace state; call unregisterTile() to finish cleanup.
     * @param {boolean} [config.autoHideStoppedVideo=true] - When true (default), the SDK hides the <video> inside a registered tile when that stream's video is not 'ON', avoiding the frozen-last-frame trap. Set false to render the frozen frame yourself.
     */
    constructor(config = {}) {
        
        if (config.serverUrl) {
            MakedoConfig.configure(config.serverUrl);
        }
        const contextId = config.contextId || config.projectId;
        if (contextId) {
            this.#contextId = contextId;
            Fetch.setContextId(contextId);
        }
        if (config.proxy) {
            this.#proxyUrl = config.proxy;
            Fetch.setProxyUrl(config.proxy); // routes token refresh through proxy for non-trial projects
            // proxy takes full precedence — projectKey is not used when a proxy is configured
        } else if (config.projectKey) {
            this.#projectKey = config.projectKey;
            Fetch.setProjectKey(config.projectKey);
        }
        if (config.features && typeof config.features === 'object') {
            Fetch.setFeatures(config.features); // expose raw flags (e.g. transcription) to the comms layer
            if (config.features.connectionQuality === true) {
                this.#features.connectionQuality = true;
            }
            if (config.features.showMemberQuality === true) {
                this.#features.showMemberQuality = true;
                this.#features.connectionQuality = true; // implied
            }
            if (config.features.showLocalQuality === true) {
                this.#features.showLocalQuality = true;
                this.#features.connectionQuality = true; // implied
            }
            if (config.features.qualityThresholds && typeof config.features.qualityThresholds === 'object') {
                this.#qualityThresholds = config.features.qualityThresholds;
            }
        }
        if (config.autoMatch && typeof config.autoMatch === 'object') {
            this.#autoMatchDefaults = {
                role:       config.autoMatch.role       || '',
                attributes: (config.autoMatch.attributes && typeof config.autoMatch.attributes === 'object')
                                ? config.autoMatch.attributes : null,
            };
        }
        if (typeof config.tileAutoRemove === 'boolean') {
            this.#tileAutoRemove = config.tileAutoRemove;
        }
        if (typeof config.autoHideStoppedVideo === 'boolean') {
            this.#autoHideStoppedVideo = config.autoHideStoppedVideo;
        }
        if (config.defaultSubscription && typeof config.defaultSubscription === 'object') {
            this.#defaultSubscription = config.defaultSubscription;
        }
        if (typeof config.trackMembers === 'boolean') {
            this.#trackMembers = config.trackMembers;
        }
    }
    
    // ========== EVENT CALLBACKS ==========
    // Set these to receive notifications
    
    /** Called after successful login. (user: {email, status, ...}) => void */
    onLogin = null;
    
    /** Called on login failure. (error: Error) => void */
    onLoginError = null;
    
    /** Called after logout. () => void */
    onLogout = null;
    
    /** Called when channels are loaded. (channels: Array) => void */
    onChannelsLoaded = null;
    
    /** Called when users are loaded. (users: Array) => void */
    onUsersLoaded = null;
    
    /** Called when a channel is selected and ready. (channel: Object) => void */
    onChannelSelected = null;
    
    /** Called when local member successfully joined the channel (you went LIVE). () => void */
    onLocalMemberJoined = null;
    
    /** Called when local member left the channel (you returned to PRE-LIVE). () => void */
    onLocalMemberLeft = null;
    
    /** Called when a remote member joins (went LIVE). (memberId: string) => void */
    onRemoteMemberJoined = null;
    
    /** Called when a remote member left LIVE state (stopped streaming). NOTE: They may still be in PRE-LIVE - check member.displayStatus. (memberId: string) => void */
    onRemoteMemberLeft = null;
    
    /** Called when a member's info/status updates. (memberId: string) => void */
    onMemberUpdate = null;
    
    /** Called when a remote member's stream starts. (memberId: string, streamType: 'camera'|'screenshare') => void */
    onRemoteMemberStreamStart = null;
    
    /** Called when a remote member's stream ends. (memberId: string, streamType: 'camera'|'screenshare') => void */
    onRemoteMemberStreamEnd = null;
    
    /** Called when local member's live status changes. (isLive: boolean) => void */
    onLocalLiveChange = null;

    /** Called when a remote member's live status changes (camera stream started or ended). (memberId: string, isLive: boolean) => void */
    onRemoteMemberLiveChange = null;
    
    /** Called when a member's media state changes (mute/unmute). (memberId: string, streamType: 'camera'|'screenshare') => void */
    onRemoteMemberMediaChange = null;
    
    /** Called when local media state changes (audio/video on/off/mute). () => void */
    onLocalMemberMediaChange = null;

    /**
     * Called when the server forcibly removes this user from the channel (e.g. meeting time limit).
     * Fires after all member/media state has already been cleaned up, deferred one paint cycle
     * so any synchronous UI updates (tile removal, media indicators) render before this runs.
     * Default implementation shows a browser alert; replace this to show custom UI instead.
     * (error: MakedoError) => void  — `error.message` for display text; `error.code` ('ROOM_KICKED'), `error.retriable`,
     * and `error.reason` / `error.source` (when the server sends a structured kick) are also available.
     */
    onKickedExt = (error) => {
        alert('You have been removed from this meeting.\n\n' + (error?.message || error || ''));
    };

    /**
     * Called when the server sends a liveliness check because media heartbeat went silent.
     * Show a "Still there?" prompt and call ackLiveliness() if the user responds.
     * If unanswered for ~5 minutes, the server will auto-exit the member.
     * () => void
     */
    onLivelinessCheck = null;

    /**
     * Called when the server gives advance notice that a kick is coming — either the whole
     * session is ending or this specific member is about to be removed. Non-destructive:
     * nothing has been torn down yet. Show a countdown/banner; the terminal `kicked` event
     * follows when the grace period expires (an immediate kick sends no warning).
     * (warning: { scope: 'self'|'room', reason: string|null, source: string|null,
     *             secondsRemaining: number|null, deadline: number|null, message: string|null }) => void
     */
    onKickWarning = null;

    /** Called when an invite arrives for this user (server WS 'newInvite').
     *  (invite: { member_id, channel_id, inviter_username, message, ... }) => void
     */
    onInviteReceived = null;

    /** Called when an invite this user sent gets a response (accepted/declined/expired).
     *  ({ member_id, channel_id, user_id, status }) => void  status: 'accepted'|'declined'|'expired'
     */
    onInviteResponse = null;

    /** Called when the project-wide availability pool changes after the user
     *  has called setAvailable({open:true}). Server pushes one event per
     *  mutation; clients should maintain a local Map<user_id, row> and apply
     *  these deltas instead of polling getAvailableUsers().
     *  (update: { type, user_id, username?, availability? }) => void
     *    type: 'added'   — new row, includes username + availability
     *    type: 'changed' — same shape as added; replace the existing row
     *    type: 'removed' — only user_id is present; drop from the local map
     */
    onAvailabilityUpdate = null;

    /** Called on overall (your-side) network-quality transitions.
     *  Only fires when init({ features: { connectionQuality: true } }) was passed.
     *  (snapshot: { level, prevLevel, reason, metrics, sampledAt }) => void
     *  level: 'good' | 'fair' | 'poor' | 'lost'
     */
    onConnectionQuality = null;

    /** Called on per-remote-member network-quality transitions (incoming-stream view).
     *  Only fires when features.connectionQuality is enabled.
     *  (memberId: string, snapshot: { level, prevLevel, reason, metrics, sampledAt }) => void
     */
    onMemberQuality = null;

    /** Called on any error. (context: string, error: Error) => void */
    onError = null;

    /** Called on any warning. (context: string, message: string) => void */
    onWarning = null;

    /** Called when any member's state changes (local or remote). (memberId: string, state: { live, video, audio, screenVideo }) => void */
    onMemberStateChange = null;

    /** Called when the session recording/transcription state changes. ({ channelId, state }) => void — state: 'recording'|'uploading'|'processing'|'idle' */
    onTranscriptionStateChange = null;

    /** Called when an AI summary becomes available for a session. ({ channelId, sessionId }) => void */
    onSessionSummaryReady = null;
    
    // ========== GETTERS ==========
    
    get isLoggedIn() {
        return this.#currentUser !== null;
    }
    
    get isInChannel() {
        return this.#inChannel;
    }
    
    get user() {
        return this.#currentUser;
    }
    
    get channels() {
        return [...this.#channels];
    }
    
    get users() {
        return [...this.#users];
    }
    
    get selectedChannel() {
        return this.#selectedChannel;
    }
    
    get localMemberId() {
        return this.#localMemberId;
    }
    
    get currentChannelId() {
        return this.#currentChannelId;
    }
    
    get currentRoomCode() {
        if (!this.#currentChannelId) return null;
        // Check cached channels first
        const channel = this.#channels.find(c => c.id === this.#currentChannelId);
        if (channel?.room_code) return channel.room_code;
        //if (channel?.pid) return channel.pid;  // Fallback for backward compatibility
        // If not found, check selectedChannel (e.g., joined by room code)
        return this.#selectedChannel?.room_code || null;
    }
    
    get localMediaState() {
        const m = this.#localMedia;
        return {
            ...m,
            videoDetail: !m.video ? 'OFF' : m.videoMuted ? 'MUTED' : 'ON',
            audioDetail: !m.audio ? 'OFF' : m.audioMuted ? 'MUTED' : 'ON',
        };
    }
    
    get screenshareState() {
        const s = this.#screenshare;
        return {
            ...s,
            videoDetail: !s.video ? 'OFF' : s.videoMuted ? 'MUTED' : 'ON',
        };
    }
    
    get hasAnyMedia() {
        return this.#localMedia.audio || this.#localMedia.video || this.#screenshare.video;
    }
    
    /**
     * Build the unified state triple for any member (local or remote).
     * @param {string} memberId
     * @returns {{ live: boolean, video: string, audio: string, screenVideo: string }}
     *   video/audio/screenVideo: 'ON' | 'MUTED' | 'OFF'
     */
    #buildMemberState(memberId) {
        if (memberId === this.#localMemberId) {
            const m = this.#localMedia;
            const s = this.#screenshare;
            const status = this.#inChannel ? 'LIVE' : this.#selectedChannel ? 'PRE-LIVE' : 'EXITED';
            const localEntry = this.#members.get(this.#localMemberId);
            return {
                displayName: localEntry?.displayName || this.#requestedDisplayName || '',
                status,
                live: this.#inChannel,
                video: !m.video ? 'OFF' : m.videoMuted ? 'MUTED' : 'ON',
                audio: !m.audio ? 'OFF' : m.audioMuted ? 'MUTED' : 'ON',
                screenVideo: !s.video ? 'OFF' : s.videoMuted ? 'MUTED' : 'ON',
            };
        }
        const member = this.#members.get(memberId);
        const states = this.getMemberMediaStates(memberId);
        const status = this.#computeDisplayStatus(member?.baseStatus);
        return {
            displayName: member?.displayName || '',
            status,
            live: status === 'LIVE',
            video: states.videoDetail,
            audio: states.audioDetail,
            screenVideo: states.screenVideoDetail,
        };
    }
    
    /**
     * Get unified state for any member (local or remote). Same shape always.
     * @param {string} memberId
     * @returns {{ status: string, live: boolean, video: string, audio: string, screenVideo: string }}
     */
    getMemberState(memberId) {
        return this.#buildMemberState(memberId);
    }
    
    /**
     * Fire the unified memberStateChange event.
     * @param {string} memberId
     */
    #fireMemberStateChange(memberId) {
        if (memberId) {
            this.#syncTileVideoVisibility(memberId);
            this.onMemberStateChange?.(memberId, this.#buildMemberState(memberId));
        }
    }

    /**
     * Inject the SDK's global stylesheet (once). Currently only defines the
     * `.vibelive-video-off` rule used by autoHideStoppedVideo. Idempotent and
     * guarded for non-DOM environments.
     * @private
     */
    #ensureSdkStyles() {
        if (typeof document === 'undefined' || !document.head) return;
        if (document.getElementById('vibelive-sdk-styles')) return;
        const style = document.createElement('style');
        style.id = 'vibelive-sdk-styles';
        // High-specificity + !important so an app's bare `video { display:block }`
        // cannot silently leave a frozen frame visible.
        style.textContent = 'video.vibelive-video-off{display:none !important;}';
        document.head.appendChild(style);
    }

    /**
     * When autoHideStoppedVideo is enabled, toggle the `.vibelive-video-off`
     * class on the <video> element inside this member's registered tile(s) so a
     * stopped/muted video stops displaying its last frozen frame. Camera and
     * screenshare tiles are handled independently. No-op when the flag is off or
     * no tile is registered. Audio is never affected (only the <video> element's
     * visibility, not srcObject).
     * @private
     * @param {string} memberId
     */
    #syncTileVideoVisibility(memberId) {
        if (!this.#autoHideStoppedVideo) return;
        const entry = this.#tileRegistry.get(memberId);
        if (!entry) return;
        const state = this.#buildMemberState(memberId);
        if (entry.camera) {
            const v = entry.camera.querySelector('video');
            if (v) v.classList.toggle('vibelive-video-off', state.video !== 'ON');
        }
        if (entry.screenshare) {
            const v = entry.screenshare.querySelector('video');
            if (v) v.classList.toggle('vibelive-video-off', state.screenVideo !== 'ON');
        }
    }
    
    // ========== DOM ELEMENT MANAGEMENT ==========
    
    /**
     * Set the local video element for camera preview.
     * If camera is already running and a different element is provided, automatically moves the stream.
     * The core will manage srcObject only - visibility is the app's responsibility.
     * @param {HTMLVideoElement} videoEl - The video element for local camera
     */
    setLocalCameraElement(videoEl) {
        // Same element re-registered — no-op (avoids srcObject reassign flicker)
        if (this.#localVideoElement === videoEl) return;

        // If already set to a different element and camera is active, reattach
        if (this.#localVideoElement && this.#localVideoElement !== videoEl) {
            this.#reattachLocalCameraElement(videoEl);
        } else {
            this.#localVideoElement = videoEl;
            // Retroactive attach: element registered after stream was already created
            if (videoEl && this.#localMedia.video) {
                const stream = this.#bridge.getLocalStream('camera');
                if (stream) {
                    videoEl.srcObject = stream;
                    videoEl.play();
                    const msg = 'Camera stream was already active when element was registered. For best results, register elements BEFORE calling toggleVideo(). Stream has been retroactively attached.';
                    console.warn('[VibeLive]', msg);
                    this.onWarning?.('setLocalCameraElement', msg);
                }
            }
        }
    }
    
    /**
     * Set the local video element for screenshare preview.
     * If screenshare is already running and a different element is provided, automatically moves the stream.
     * The core will manage srcObject only - visibility is the app's responsibility.
     * @param {HTMLVideoElement} videoEl - The video element for local screenshare
     */
    setLocalScreenElement(videoEl) {
        // Same element re-registered — no-op (avoids srcObject reassign flicker)
        if (this.#localScreenElement === videoEl) return;

        // If already set to a different element and screenshare is active, reattach
        if (this.#localScreenElement && this.#localScreenElement !== videoEl) {
            this.#reattachLocalScreenElement(videoEl);
        } else {
            this.#localScreenElement = videoEl;
            // Retroactive attach: element registered after stream was already created
            if (videoEl && this.#screenshare.video) {
                const stream = this.#bridge.getLocalStream('screenshare');
                if (stream) {
                    videoEl.srcObject = stream;
                    videoEl.play();
                    const msg = 'Screenshare stream was already active when element was registered. For best results, register elements BEFORE calling toggleScreenshare(). Stream has been retroactively attached.';
                    console.warn('[VibeLive]', msg);
                    this.onWarning?.('setLocalScreenElement', msg);
                }
            }
        }
    }
    
    /**
     * Unregister the local camera video element.
     * Clears srcObject and removes the reference.
     */
    clearLocalCameraElement() {
        if (this.#localVideoElement) {
            this.#localVideoElement.srcObject = null;
            this.#localVideoElement = null;
        }
    }
    
    /**
     * Unregister the local screenshare video element.
     * Clears srcObject and removes the reference.
     */
    clearLocalScreenElement() {
        if (this.#localScreenElement) {
            this.#localScreenElement.srcObject = null;
            this.#localScreenElement = null;
        }
    }
    
    /**
     * PRIVATE: Re-register a new local camera video element.
     * Automatically handles clearing the old element and attaching the stream to the new one.
     * Called automatically by setLocalCameraElement() when switching elements.
     * @private
     * @param {HTMLVideoElement} videoEl - The new video element for local camera
     */
    #reattachLocalCameraElement(videoEl) {
        // Clear old element's srcObject
        if (this.#localVideoElement) {
            this.#localVideoElement.srcObject = null;
        }
        
        // Register new element
        this.#localVideoElement = videoEl;
        
        // Attach current stream if camera is active
        if (this.#localMedia.video) {
            const stream = this.#bridge.getLocalStream('camera');
            if (stream && this.#localVideoElement) {
                this.#localVideoElement.srcObject = stream;
                this.#localVideoElement.play();
            }
        }
    }
    
    /**
     * PRIVATE: Re-register a new local screenshare video element.
     * Automatically handles clearing the old element and attaching the stream to the new one.
     * Called automatically by setLocalScreenElement() when switching elements.
     * @private
     * @param {HTMLVideoElement} videoEl - The new video element for local screenshare
     */
    #reattachLocalScreenElement(videoEl) {
        // Clear old element's srcObject
        if (this.#localScreenElement) {
            this.#localScreenElement.srcObject = null;
        }
        
        // Register new element
        this.#localScreenElement = videoEl;
        
        // Attach current stream if screenshare is active
        if (this.#screenshare.video) {
            const stream = this.#bridge.getLocalStream('screenshare');
            if (stream && this.#localScreenElement) {
                this.#localScreenElement.srcObject = stream;
                this.#localScreenElement.play();
            }
        }
    }
    
    /**
     * Register a video element for a remote member's camera stream.
     * The core will manage srcObject only - visibility is the app's responsibility.
     * @param {string} memberId 
     * @param {HTMLVideoElement} videoEl 
     */
    setRemoteCameraElement(memberId, videoEl) {
        this.#setRemoteElement(memberId, 'camera', videoEl);
    }
    
    /**
     * Register a video element for a remote member's screenshare stream.
     * The core will manage srcObject only - visibility is the app's responsibility.
     * @param {string} memberId 
     * @param {HTMLVideoElement} videoEl 
     */
    setRemoteScreenElement(memberId, videoEl) {
        this.#setRemoteElement(memberId, 'screenshare', videoEl);
    }
    
    /**
     * Internal helper for setting remote video elements
     * @private
     */
    #setRemoteElement(memberId, streamType, videoEl) {
        let member = this.#members.get(memberId);
        if (!member) {
            // registerTile() can legitimately run before the member roster resolves:
            // enterByChannelId() pre-fetches members asynchronously and channelSelected
            // fires first, so an app registering a tile at channelSelected used to have
            // its element silently dropped here (the tile registry kept the tile — class
            // sync and auto-remove worked — but videoElements lost the reference, so the
            // stream never attached). Never drop a registration: create a placeholder
            // entry; roster fetches enrich it in place rather than replacing it.
            member = this.#createMemberEntry({});
            this.#members.set(memberId, member);
        }
        if (!member.videoElements) {
            member.videoElements = {};
        }
        // Same element re-registered — no-op (avoids srcObject reassign flicker)
        if (member.videoElements[streamType] === videoEl) return;

        member.videoElements[streamType] = videoEl;

        // If stream already exists, attach it (visibility is app's responsibility)
        const stream = this.#bridge?.getMemberMediaStream(memberId, streamType);
        if (stream && videoEl) {
            videoEl.srcObject = stream;
        }
    }
    
    /**
     * Unregister a member's camera video element (e.g., when removing tile from DOM)
     * @param {string} memberId 
     */
    clearRemoteCameraElement(memberId) {
        this.#clearRemoteElement(memberId, 'camera');
    }
    
    /**
     * Unregister a member's screenshare video element (e.g., when removing tile from DOM)
     * @param {string} memberId 
     */
    clearRemoteScreenElement(memberId) {
        this.#clearRemoteElement(memberId, 'screenshare');
    }
    
    /**
     * Internal helper for clearing remote video elements
     * @private
     */
    #clearRemoteElement(memberId, streamType) {
        const member = this.#members.get(memberId);
        if (member?.videoElements?.[streamType]) {
            member.videoElements[streamType].srcObject = null;
            delete member.videoElements[streamType];
        }
    }

    // ========== TILE REGISTRY ==========

    /**
     * Register a DOM element as a tile for a member's stream.
     * When registered, the SDK will:
     *   - Auto-find the <video> element inside and register it (setLocalCamera/setRemoteCamera etc.)
     *   - Auto-remove the tile from the DOM when the member exits, you leave the room, or get kicked
     *   - Auto-remove screenshare tiles when the screenshare stream ends
     *
     * Apps still own tile creation (DOM structure, CSS, indicators) -- the SDK only owns lifecycle cleanup.
     *
     * @param {string} memberId - The member this tile belongs to
     * @param {'camera'|'screenshare'} streamType - Which stream type
     * @param {HTMLElement} element - The tile DOM element (must contain a <video> child)
     */
    registerTile(memberId, streamType, element) {
        if (!element) return;

        // Store in registry
        let entry = this.#tileRegistry.get(memberId);
        if (!entry) {
            entry = { camera: null, screenshare: null };
            this.#tileRegistry.set(memberId, entry);
        }

        // Idempotency: if this exact element is already registered, no-op.
        // This makes registerTile safe to call twice on the same element (e.g. when
        // a memberStateChange handler auto-creates a tile before remoteStreamStart fires).
        if (entry[streamType] === element) return;

        entry[streamType] = element;

        // Auto-register the <video> element inside the tile
        const videoEl = element.querySelector('video');
        if (!videoEl) {
            this.onWarning?.('registerTile', `No <video> element found inside registered tile for member ${memberId} (${streamType}). Stream will not be visible. Ensure your tile element contains a <video> tag.`);
        }
        if (videoEl) {
            const isLocal = memberId === this.#localMemberId;
            if (isLocal) {
                if (streamType === 'camera') this.setLocalCameraElement(videoEl);
                else if (streamType === 'screenshare') this.setLocalScreenElement(videoEl);
            } else {
                if (streamType === 'camera') this.setRemoteCameraElement(memberId, videoEl);
                else if (streamType === 'screenshare') this.setRemoteScreenElement(memberId, videoEl);
                // Custom/arbitrary label (e.g. a thumbnail or app-published stream): bind generically.
                else this.#setRemoteElement(memberId, streamType, videoEl);
            }
            // Ensure the auto-hide stylesheet exists before any tile renders.
            if (this.#autoHideStoppedVideo) this.#ensureSdkStyles();
        }

        // Inject watermark if not already present
        if (!this.#selectedChannel?.hide_watermark && !element.querySelector('.makedo-watermark')) {
            const wm = document.createElement('div');
            wm.className = 'makedo-watermark';
            wm.innerHTML = 'Powered by\u00A0<span style="color:#5DCAA5;font-weight:500;">VibeLive</span>';
            wm.style.cssText = 'position:absolute;top:8px;left:8px;display:inline-flex;align-items:center;font-size:10px;color:rgba(255,255,255,.92);background:rgba(15,23,41,.65);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);padding:3px 8px;border-radius:999px;border:0.5px solid rgba(255,255,255,.12);letter-spacing:0.2px;pointer-events:none;z-index:10;';
            element.appendChild(wm);
        }

        // If a quality monitor is running, re-apply any current bar state for this tile.
        if (streamType === 'camera') {
            this.#applyCurrentQualityBar(memberId);
        }

        // Reflect the member's current video state on the freshly registered tile so
        // a tile created while video is already OFF/MUTED does not flash a frozen or
        // stale frame before the next memberStateChange fires.
        this.#syncTileVideoVisibility(memberId);
    }

    /**
     * Manually unregister and remove a tile from the DOM. Always performs a full DOM
     * removal regardless of the autoRemove flag set at registerTile() time. Use this
     * to finish a grace animation and clean up when autoRemove:false was set.
     * @param {string} memberId
     * @param {'camera'|'screenshare'} streamType
     */
    unregisterTile(memberId, streamType) {
        this.#removeTile(memberId, streamType, { forceRemove: true });
    }

    /**
     * Internal: remove a registered tile, clearing its stream and optionally its DOM element.
     *
     * When autoRemove is true (the default): clears srcObject + removes element from DOM.
     * When autoRemove is false: clears srcObject only — element stays in DOM so the app
     *   can show a grace state. The app must call unregisterTile() to finish cleanup.
     * forceRemove overrides the autoRemove flag (used by unregisterTile() and kick path).
     *
     * @private
     * @param {string} memberId
     * @param {'camera'|'screenshare'} streamType
     * @param {Object} [opts]
     * @param {boolean} [opts.forceRemove=false] - Ignore autoRemove flag; always remove from DOM.
     */
    #removeTile(memberId, streamType, { forceRemove = false } = {}) {
        const entry = this.#tileRegistry.get(memberId);
        if (!entry || !entry[streamType]) return;

        const element = entry[streamType];
        const shouldRemoveFromDom = forceRemove || this.#tileAutoRemove;

        const isLocal = memberId === this.#localMemberId;

        if (shouldRemoveFromDom) {
            // Full removal: clear the SDK's video-element reference (nulls srcObject + deletes
            // member.videoElements entry), then remove the tile from the DOM.
            if (isLocal) {
                if (streamType === 'camera') this.clearLocalCameraElement();
                else if (streamType === 'screenshare') this.clearLocalScreenElement();
            } else {
                if (streamType === 'camera') this.clearRemoteCameraElement(memberId);
                else if (streamType === 'screenshare') this.clearRemoteScreenElement(memberId);
            }
            element.remove();
        } else {
            // tileAutoRemove:false — stream stops, element stays in DOM for grace state.
            // IMPORTANT: do NOT call clearRemoteCameraElement here. That method deletes
            // member.videoElements[streamType], which would prevent #updateRemoteVideoVisibility
            // from rebinding the new stream when the member reconnects. Keeping the reference
            // alive means the reconnect stream attaches automatically on remoteStreamStart.
            // Null srcObject directly to stop rendering without losing the element reference.
            const videoEl = element.querySelector('video');
            if (videoEl) videoEl.srcObject = null;
        }

        entry[streamType] = null;
        if (!entry.camera && !entry.screenshare) {
            this.#tileRegistry.delete(memberId);
        }
    }

    /**
     * Internal: remove ALL registered tiles for a member (both camera + screenshare).
     * @private
     */
    #removeAllTiles(memberId) {
        this.#removeTile(memberId, 'camera');
        this.#removeTile(memberId, 'screenshare');
    }

    /**
     * Internal: remove ALL registered tiles for ALL members (room exit / kicked).
     * @private
     */
    #removeAllRegisteredTiles() {
        for (const memberId of this.#tileRegistry.keys()) {
            this.#removeAllTiles(memberId);
        }
        this.#tileRegistry.clear();
    }
    
    // ========== MEMBER DATA ACCESS ==========
    
    /**
     * Get member info by ID
     * @param {string} memberId 
     * @returns {Object|null} { displayName, baseStatus, displayStatus, hasCamera, hasScreenshare }
     *   displayStatus: 'LIVE' (streaming), 'PRE-LIVE' (preparing), 'EXITED' (left the room)
     */
    getMember(memberId) {
        const member = this.#members.get(memberId);
        if (!member) return null;
        
        // Return a copy with computed displayStatus
        return {
            displayName: member.displayName,
            baseStatus: member.baseStatus,
            displayStatus: this.#computeDisplayStatus(member.baseStatus),
            hasCamera: member.hasCamera,
            hasScreenshare: member.hasScreenshare
        };
    }
    
    /**
     * Get all member IDs
     * @returns {string[]}
     */
    getMemberIds() {
        return [...this.#members.keys()];
    }
    
    /**
     * Get media states for a member (from Bridge)
     * @param {string} memberId 
     * @returns {Object|null} { audioDetail, videoDetail, screenAudioDetail, screenVideoDetail }
     * Values: 'ON' | 'MUTED' | 'OFF'
     */
    getMemberMediaStates(memberId) {
        const states = this.#bridge?.getMemberMediaStates(memberId);
        const norm = v => v === 'ON BUT NO STREAM' ? 'ON' : v;
        if (!states) {
            return { audioDetail: 'OFF', videoDetail: 'OFF', screenAudioDetail: 'OFF', screenVideoDetail: 'OFF' };
        }
        return {
            audioDetail:       norm(states.cam_audio_detail),
            videoDetail:       norm(states.cam_video_detail),
            screenAudioDetail: norm(states.screen_audio_detail),
            screenVideoDetail: norm(states.screen_video_detail)
        };
    }
    
    /**
     * Get the MediaStream for a member
     * @param {string} memberId 
     * @param {string} streamType - 'camera' or 'screenshare'
     * @returns {MediaStream|null}
     */
    getMemberStream(memberId, streamType) {
        return this.#bridge?.getMemberMediaStream(memberId, streamType) || null;
    }

    // ==================== Custom labeled upstreams ====================
    /**
     * Publish an arbitrary-label upstream alongside camera/screenshare — e.g. a low-bandwidth
     * thumbnail, a processed canvas stream, or a second camera. Reserved labels
     * ('camera'/'screenshare'/'microphone'/'movie') are rejected; use the named APIs for those.
     * The label must be unique among your active upstreams. Remote peers discover it via
     * remoteStreamStart (under the default subscription) and read it with getStream(id, label).
     * @param {string} label - unique custom stream label
     * @param {Object} opts - exactly ONE source: { stream?: MediaStream } |
     *   { constraints?: MediaStreamConstraints } | { from?: string }, plus
     *   { width?, height?, fps?, fit?, encodings?: RTCRtpEncodingParameters[],
     *     contentHint?: 'motion'|'detail'|'text'|'' }
     *   from — derive this stream by downscaling another label's video ('camera',
     *     'screenshare', ...). Requires width and height; fps defaults to 15 and fit to
     *     'cover' (centre-crop, no letterboxing — right for uniform grid tiles). One read
     *     feeds every derived stream, so a mosaic thumbnail costs a blit, not a capture, and
     *     the source label keeps publishing its own native track untouched.
     *   contentHint defaults to 'motion' (favors frame rate — the right choice for most
     *   processed/thumbnail streams); pass 'detail' for text/screen-like content.
     * @returns {Promise<MediaStream>} the published stream
     */
    async publishStream(label, opts) {
        return this.#bridge.publishStream(label, opts);
    }
    /** Stop and remove a custom labeled upstream previously published with publishStream(). */
    unpublishStream(label) {
        return this.#bridge.unpublishStream(label);
    }

    // ==================== Downstream subscription ====================
    /**
     * Control which tracks the SFU sends you for ONE remote stream.
     * @param {string} memberId
     * @param {string} label - stream label ('camera', 'screenshare', or a custom label)
     * @param {string[]} kinds - ['audio','video'] | ['video-low'] | ['audio'] | []  ([] = receive nothing)
     * @returns {boolean} false if that stream is not currently present locally
     */
    subscribe(memberId, label, kinds) {
        return this.#bridge.subscribe(memberId, label, kinds);
    }
    /** Stop receiving a specific remote stream (requests no tracks; the stream stays discoverable). */
    unsubscribe(memberId, label) {
        return this.#bridge.unsubscribe(memberId, label);
    }
    /**
     * Set the default subscription map applied to all streams on join (and re-applied per channel).
     * The '' key is the wildcard for any unlisted label. Default: { '': ['audio','video'] }.
     * Narrowing this (e.g. { '': [] }) saves bandwidth in large rooms but disables auto-discovery
     * of unseen labels — pair with an app-level announce or a metadata-only default.
     */
    setDefaultSubscription(map) {
        this.#defaultSubscription = map; // remember across bridge/session recreation
        return this.#bridge?.setDefaultSubscription(map);
    }
    /** Discovery: labels this member is currently publishing that we have received. */
    getMemberStreamLabels(memberId) {
        return this.#bridge.getMemberStreamLabels(memberId);
    }

    /**
     * Get all members currently in the channel (including self).
     * Returns member data from the channel's member list.
     * Also populates internal member tracking for status updates.
     * @returns {Promise<Array>} Array of member objects with { id, displayName, baseStatus, displayStatus }
     *   displayStatus: 'LIVE' (streaming), 'PRE-LIVE' (preparing), 'EXITED' (left the room)
     */
    async getCurrentChannelMembers() {
        if (!this.#currentChannelId) {
            return [];
        }
        
        try {
            // Fetch fresh member list from server (includes self)
            const members = await this.#bridge.getMembers({
                channel_id: this.#currentChannelId,
                depth: 1
            });
            
            // Populate internal member tracking and return normalized format
            return members.map(member => {
                const baseStatus = member.status || 'invited';

                const existing = this.#members.get(member.id);
                if (!existing) {
                    this.#members.set(member.id, this.#createMemberEntry({ ...member, status: baseStatus }));
                } else {
                    // Enrich in place — never replace: the entry may already carry
                    // videoElements from a tile registered before this fetch resolved.
                    existing.displayName = member.display_name || existing.displayName;
                    existing.baseStatus = baseStatus;
                }
                // Seed mute state from server data into bridge.membersMap if entry already exists
                this.#seedMuteStateFromServerData(member.id, member);
                
                // Return normalized camelCase format
                return {
                    id: member.id,
                    displayName: member.display_name || 'Unknown',
                    baseStatus: baseStatus,
                    displayStatus: this.#computeDisplayStatus(baseStatus)
                };
            });
        } catch (error) {
            console.error('Error fetching channel members:', error);
            return [];
        }
    }
    
    /**
     * Compute display status from raw status value (private helper)
     * @param {string} status - Raw status value from backend
     * @returns {string} 'LIVE', 'PRE-LIVE', or 'EXITED'
     */
    #computeDisplayStatus(status) {
        if (!status) return 'EXITED';
        
        const statusLower = status.toLowerCase();
        
        if (statusLower === 'nowinside') {
            return 'LIVE';
        } else if (statusLower === 'lobby_pre' || statusLower === 'lobby_mid') {
            return 'PRE-LIVE';
        } else {
            return 'EXITED';
        }
    }

    /**
     * Create a standardized member entry for the internal #members Map.
     * Single source of truth for the member object shape — add new fields here.
     * @private
     * @param {Object} memberData - Raw member data (from API or WebSocket)
     * @param {string} [memberData.display_name] - Display name from server
     * @param {string} [memberData.status] - Status from server (e.g. 'nowinside', 'invited')
     * @returns {{ displayName: string, baseStatus: string, hasCamera: boolean, hasScreenshare: boolean, videoElements: Object }}
     */
    #createMemberEntry(memberData = {}) {
        return {
            displayName: memberData.display_name || 'Unknown',
            baseStatus: memberData.status || 'invited',
            hasCamera: false,
            hasScreenshare: false,
            videoElements: {},
            // Mute state from server (in-memory only on ThingMember, not DB-backed).
            // Stored here so #handleMemberStreamStart can seed bridge.membersMap
            // even when the member was already known before the stream arrived.
            camAudioMuted:    memberData.camAudioMuted    === true,
            camVideoMuted:    memberData.camVideoMuted    === true,
            screenVideoMuted: memberData.screenVideoMuted === true,
        };
    }

    /**
     * Normalize channel object - strip raw members array to prevent misuse
     * Apps should use getCurrentChannelMembers() for normalized member data
     * @param {Object} channel - Raw channel from API
     * @returns {Object} Normalized channel with memberCount instead of members array
     */
    #normalizeChannel(channel) {
        if (!channel) return channel;
        const { members, ...rest } = channel;
        const list = Array.isArray(members) ? members : [];

        // Parse the app-defined metadata blob (server stores it as an opaque JSON string).
        let metadata = null;
        if (typeof rest.metadata === 'string' && rest.metadata.trim()) {
            try { metadata = JSON.parse(rest.metadata); } catch (_) { metadata = null; }
        }

        // Owner presence, derived from the members the server returned (is_owner is set
        // at depth>=1). Lets an app distinguish a room's Offline / Preparing / Live
        // lifecycle from getChannelInfo() alone, without joining:
        //   ownerPresent  — owner is in the room (lobby or streaming)
        //   ownerLive     — owner is actively streaming (nowinside)
        const IN_ROOM_STATUS = new Set(['lobby_pre', 'lobby_mid', 'nowinside']);
        const owner = list.find(m => m.is_owner);
        return {
            ...rest,
            metadata,
            memberCount: list.length, // reserved spots (includes stepped-out members)
            presentCount: list.filter(m => IN_ROOM_STATUS.has(m.status)).length, // actually in the room now
            ownerMemberId: owner?.id ?? null,
            ownerDisplayName: owner?.display_name ?? null,
            ownerPresent: !!(owner && IN_ROOM_STATUS.has(owner.status)),
            ownerLive: !!(owner && owner.status === 'nowinside'),
            // Requesting user owns this room. Prefer the server's viewer-derived me_owner
            // (authoritative, independent of member-list contents); fall back to deriving
            // from the members array for servers that predate the field.
            amOwner: rest.me_owner === true || !!(owner && owner.is_me)
        };
    }
    
    // ========== AUTHENTICATION ==========
    
    /**
     * Login to the system with email/password
     * @param {string} email 
     * @param {string} password 
     * @param {string} contextId - Optional context ID (overrides constructor value)
     * @returns {Promise<Object>} User object on success
     */
    async login(email, password, contextId = null) {
        try {
            this.#bridge = new Bridge();
            this.#setupGlobalCallbacks();
            
            // Set contextId centrally if provided (overrides constructor value)
            if (contextId) {
                this.#contextId = contextId;
                Fetch.setContextId(contextId);
            }
            
            // contextId is auto-injected by Fetch.requestData()
            const loginRequest = { email, password };
            
            const result = await this.#bridge.login(loginRequest);
            
            if (result.status === 'loggedIn') {
                this.#currentUser = result;
                Fetch.setUserId(result.id || result.pid);
                
                this.onLogin?.(result);
                return result;
            } else {
                const error = new Error(result.message || 'Login failed');
                this.onLoginError?.(error);
                throw error;
            }
        } catch (error) {
            this.onLoginError?.(error);
            this.onError?.('login', error);
            throw error;
        }
    }
    
    /**
     * Sign up and login as an anonymous user.
     * Supports two modes:
     *   - Proxy mode (recommended): contextAuthToken stays server-side, proxy handles user creation
     *   - Legacy mode: contextAuthToken provided directly to the frontend
     * @param {string} username - Display name for the user
     * @param {string} [contextId] - Your app's context ID (optional if passed to constructor)
     * @param {string} [contextAuthToken] - Your app's context auth token (optional if proxy is configured)
     * @returns {Promise<Object>} User object with { userId, username, email (pre_email) }
     */
    async signupAnonymous(username, contextId = null) {
        try {
            // Use provided values or fall back to constructor values
            const ctxId = contextId || this.#contextId;
            const proxyUrl = this.#proxyUrl;

            if (!ctxId) {
                throw new MakedoError(ErrorCodes.INIT_INVALID_CONFIG, 'contextId (or projectId) is required. Pass it to VibeLive.init({ projectId: "..." }).');
            }
            
            // Set contextId centrally — auto-attached to every Fetch request
            Fetch.setContextId(ctxId);
            
            this.#bridge = new Bridge();
            this.#setupGlobalCallbacks();
            
            // Store the app-provided name for use as member display_name when entering channels
            this.#requestedDisplayName = username || null;
            
            // Try to restore an existing session from cookie before creating a new user.
            // Try to reuse an existing session from the cookie.
            // restoreSession() calls getSelf() which includes the X-Project-Auth-Token
            // header when a projectKey is configured, so the backend validates both the
            // user token AND context authorization in a single round-trip.
            // Only proxy mode needs forced re-auth (the proxy mints a contextAuthToken
            // that the SDK can't produce on its own).
            const restoredUser = await this.#bridge.restoreSession();
            if (restoredUser && !proxyUrl) {
                // Session is valid — backend already verified context access
                console.debug('VibeLiveCore: Restored existing session for user:', restoredUser.id);
                
                const restoredUsername = restoredUser.username || username || restoredUser.pre_email;
                this.#currentUser = {
                    ...restoredUser,
                    userId: restoredUser.id,
                    username: restoredUsername,
                    isAnonymous: true,
                    isRestored: true
                };
                // Sync the member display-name to the server's stored username so it stays
                // consistent across reloads (rather than picking up a new random name each visit).
                if (restoredUsername) { this.#requestedDisplayName = restoredUsername; }
                Fetch.setUserId(restoredUser.id);
                this.#contextId = ctxId;
                
                // Identity is finalized — now start WebSocket with the restored token
                this.#bridge.startDispatcher();
                await this.#bridge.protocolReady;
                
                this.onLogin?.(this.#currentUser);
                return this.#currentUser;
            }
            
            // No existing session — or proxy mode requires re-auth through the developer's endpoint.
            // Do NOT start a dispatcher here — identity is not finalized yet.
            if (restoredUser) {
                console.debug('VibeLiveCore: Session cookie found but proxy re-auth required. Re-authorizing...');
            }
            // Tear down any existing WebSocket BEFORE clearing the auth token.
            // If restoreSession() started a dispatcher, the WS connection may still be
            // opening asynchronously. If we clear the token first, the WS opens with no
            // token, fails auth silently, and the dispatcher guard (if (!this.dispatcher))
            // then prevents the post-reauth initDispatcher() from creating a fresh socket.
            // resetDispatcher() closes the socket and nulls the reference so the next
            // initDispatcher() call (inside authorizeViaProxy / signupAnonInContext) creates
            // a fresh, correctly-authenticated connection.
            this.#bridge.resetDispatcher();
            // Clear stale auth token before creating a new user.
            // Otherwise Fetch.requestData() auto-injects the old token, and the server
            // tries to extend it for the *new* user — which fails and returns null.
            Fetch.clearAuthToken();
            let result;
            
            if (proxyUrl) {
                // Production mode: POST to developer's proxy, which adds contextAuthToken server-side
                result = await this.#bridge.authorizeViaProxy(proxyUrl, {
                    contextId: ctxId,
                    username: username || 'Unknown'
                });
            } else {
                // Trial mode: contextId alone is sufficient for trial projects.
                // Non-trial projects without a proxy will get HTTP 403 with a subcode — see ERROR_GUIDE.
                const autoPassword = 'temp_' + Math.random().toString(36).substring(7);
                result = await this.#bridge.signupAnonInContext({
                    // contextId is auto-injected by Fetch.requestData()
                    username: username || 'Unknown',
                    password: autoPassword
                });
            }
            
            if (result.status === 'loggedIn') {
                this.#currentUser = {
                    ...result,
                    userId: result.id || result.pid,
                    username: username,
                    isAnonymous: true
                };
                Fetch.setUserId(result.id || result.pid);
                this.#contextId = ctxId;
                
                // Wait for protocol to be ready
                await this.#bridge.protocolReady;
                
                this.onLogin?.(this.#currentUser);
                return this.#currentUser;
            } else {
                // Server rejected the signup — likely a non-trial contextId used without a proxy.
                let msg;
                if (!proxyUrl && result.error === 'failed to authorize request') {
                    msg = `This contextId requires server-side authorization. Set up a proxy endpoint that adds the auth token and pass it as proxy to VibeLive.init({ contextId, proxy: '/my-proxy' }).`;
                } else {
                    msg = result.message || result.note || result.error || `Anonymous signup failed`;
                }
                const error = new MakedoError(ErrorCodes.AUTH_FAILED, msg, { context: 'signupAnonymous', retriable: false });
                this.onLoginError?.(error);
                throw error;
            }
        } catch (error) {
            this.onLoginError?.(error);
            this.onError?.('signupAnonymous', error);
            throw error;
        }
    }
    
    /**
     * Create a new channel (room) and get its room code.
     * Must be logged in first.
     * @param {Object} options
     * @param {string} options.title - Room title
     * @param {boolean} [options.allowsGuests=true] - Allow anonymous users to join (flat DB column)
     * @param {number} [options.maxSize=null] - Max number of members; default 8 (flat DB column)
     * @param {string} [options.statusOnMeetingEnd=null] - Room status after meeting ends.
     *   One of: 'open' (default — rejoinable), 'closed', 'pending', 'disabled', 'archived'.
     *   Overrides the project-level setting for this room only.
     * @param {number} [options.maxLength=null] - Max session length in minutes. Acts as a per-room
     *   cap and cannot exceed the plan-tier ceiling. Cascade: room cap → plan limit.
     * @param {boolean} [options.enableTextChat=null] - Enable the text chat sidebar.
     *   Cascade: room → project → true.
     * @param {boolean} [options.allowScreensharing=null] - Allow screensharing in this room.
     *   Cascade: room → project → true.
     * @param {boolean} [options.allowLurkers=null] - Allow members to join without publishing media.
     *   Cascade: room → project → false.
     * @param {boolean} [options.useEmailWhitelist=null] - Restrict entry to whitelisted emails only.
     *   Cascade: room → project → false.
     * @param {boolean} [options.allowFileSharing=null] - Allow members to share files in this room.
     *   Cascade: room → project → false.
     * @param {boolean} [options.transcriptionActive=null] - Turn transcription/recording ON for this
     *   room. Only has effect when the project's transcriptionAllowed capability is true. When omitted,
     *   falls back to the app-wide default from init({ features: { transcriptionActive: true } }).
     *   Cascade: room → project → false.
     * @returns {Promise<Object>} Channel object with { id, room_code, title }
     */
    async createChannel({ title, allowsGuests = true, maxSize = null, statusOnMeetingEnd = null, maxLength = null, enableTextChat = null, allowScreensharing = null, allowLurkers = null, useEmailWhitelist = null, allowFileSharing = null, transcriptionActive = null }) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to create a room. Call signup() or login() first.', { context: 'createChannel' });
        }
        
        try {
            const boolCfg = v => (v === null || v === undefined) ? null : (v ? 'true' : 'false');
            // transcriptionActive: explicit per-room value wins; otherwise fall back to the app-wide
            // default set via init({ features: { transcriptionActive: true } }).
            const effectiveTranscriptionActive = (transcriptionActive !== null && transcriptionActive !== undefined)
                ? transcriptionActive
                : (Fetch.features?.transcriptionActive ?? null);
            const channel = await this.#bridge.createNewChannel({
                title: title,
                allows_guests: allowsGuests,
                max_size: maxSize,
                display_name: this.#requestedDisplayName,  // Set owner's display name
                statusOnMeetingEnd: statusOnMeetingEnd,
                maxLength: maxLength,
                enableTextChat: boolCfg(enableTextChat),
                allowScreensharing: boolCfg(allowScreensharing),
                allowLurkers: boolCfg(allowLurkers),
                useEmailWhitelist: boolCfg(useEmailWhitelist),
                allowFileSharing: boolCfg(allowFileSharing),
                transcriptionActive: boolCfg(effectiveTranscriptionActive),
                // contextId is auto-injected by Fetch.requestData()
            });
            
            console.debug('[VibeLiveCore] Channel created:', channel.room_code+" - "+channel.title);
            return {
                id: channel.id,
                room_code: channel.room_code,  // This is the short room code
                title: channel.title
            };
        } catch (error) {
            this.onError?.('createChannel', error);
            throw error;
        }
    }
    
    /**
     * Enter a channel by its database ID (puts you in PRE-LIVE state).
     * This is the fundamental entry method - all other entry methods call this.
     * Call startLive() after this to connect WebRTC.
     * @param {string} channelId - The channel's database ID
     * @param {Object} [channelData] - Optional pre-fetched channel data to avoid re-fetching
     * @param {string} [display_name] - Optional display name to use for this channel (updates existing member or sets for new member)
     * @returns {Promise<Object>} The channel object with full metadata
     */
    async enterByChannelId(channelId, channelData = null, display_name = null) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to enter a room. Call signup() or login() first.', { context: 'enterByChannelId' });
        }
        if (!channelId && !channelData) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'enterByChannelId requires a channelId or channelData object.', { context: 'enterByChannelId' });
        }
        
        try {
            // Leave old channel if exists
            if (this.#currentChannelId && this.#bridge) {
                this.#bridge.leaveServer(false);
            }
            
            // Clear previous state
            this.#clearMembers();
            this.#localMemberId = null;
            this.#currentChannelId = null;
            this.#inChannel = false;
            
            // Wait for protocol.js to load
            await this.#bridge.protocolReady;
            
            // Fetch channel data if not provided OR if provided data is incomplete (missing sfu_url)
            let channel = channelData;
            if (!channel || !channel.sfu_url) {
                channel = await this.getChannelById(channelId);
            }
            
            // Get or create member using the channel ID
            // Priority: explicit display_name arg → requestedDisplayName (from signup) → currentUser.username
            // This ensures the member always gets the user's actual username as a display name
            // unless the app explicitly supplies a different one.
            const effectiveDisplayName = display_name || this.#requestedDisplayName || this.#currentUser?.username || null;
            const member = await this.#bridge.getOrCreateSelfMember({
                channel_id: channel.id,
                display_name: effectiveDisplayName
            });
            
            this.#localMemberId = member.id;
            this.#currentChannelId = channel.id;
            this.#selectedChannel = channel;
            
            // Enable browser activity monitoring (tab closure detection)
            BrowserActivity.setActiveChannel(channel.id);
            // Sync media state that may have been set up in PRE-LIVE before entering.
            // connectLocalAudio is normally called by setAudio/setVideo/setMicrophone,
            // but if audio was started before this point, the analyser won't be connected
            // yet when the first heartbeat samples fire.
            if (this.#localMedia.audio) {
                const stream = this.#bridge.getLocalStream('camera');
                if (stream) BrowserActivity.connectLocalAudio(stream);
            }
            BrowserActivity.setLocalVideoActive(this.#localMedia.video || this.#screenshare.video);
            // Route media heartbeats via WebSocket when available.
            // Capture memberId in the closure — it's stable for the lifetime of this channel entry.
            const _memberId = member.id;
            BrowserActivity.setMediaHeartbeatSender((channelId, stats) => this.#bridge.recordMediaHeartbeat(channelId, _memberId, stats));
            
            // Add self to members map immediately so status updates work
            this.#members.set(member.id, this.#createMemberEntry(member));            
            // Setup server connection using channel ID
            this.#bridge.setupServer(channel.id, channel.sfu_url, member.id, member.access_code);
            
            // Setup callbacks
            this.#setupChannelCallbacks();

            // Start the optional connection-quality monitor (Phase 1 — local-only).
            // Feature is opt-in via init({ features: { connectionQuality: true } }).
            this.#startQualityMonitor(member.id);
            
            // Pre-populate existing members for immediate status update support
            // This runs async but completes before most WebSocket events arrive
            // If events do arrive early, #ensureMember() handles lazy fetching
            this.#bridge.getMembers({
                channel_id: channel.id,
                depth: 1
            }).then(existingMembers => {
                if (existingMembers && existingMembers.length > 0) {
                    existingMembers.forEach(m => {
                        // Don't overwrite self; enrich entries that already exist (a tile
                        // may have been registered for them during channelSelected).
                        if (m.id === member.id) return;
                        const existing = this.#members.get(m.id);
                        if (!existing) {
                            this.#members.set(m.id, this.#createMemberEntry(m));
                        } else {
                            existing.displayName = m.display_name || existing.displayName;
                            if (m.status) existing.baseStatus = m.status;
                        }
                    });
                }
            }).catch(error => {
                console.warn('Could not pre-fetch existing members:', error);
            });
            
            this.onChannelSelected?.(channel);
            return channel;
        } catch (error) {
            throw error;
        }
    }
    
    /**
     * Enter a channel by its room code (puts you in PRE-LIVE state).
     * This is a convenience wrapper around enterByChannelId().
     * Call startLive() after this to connect WebRTC.
     * @param {string} roomCode - The short room code (channel.room_code)
     * @param {string} [display_name] - Optional display name to use for this channel (updates existing member or sets for new member)
     * @returns {Promise<Object>} The channel object with full metadata
     */
    async enterByRoomCode(roomCode, display_name = null) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to enter a room. Call signup() or login() first.', { context: 'enterByRoomCode' });
        }
        if (!roomCode || typeof roomCode !== 'string' || roomCode.trim().length === 0) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, `enterByRoomCode requires a non-empty room code string. Got: ${JSON.stringify(roomCode)}`, { context: 'enterByRoomCode' });
        }
        
        try {
            // Fetch channel data by room code
            const channel = await this.getChannelByRoomCode(roomCode);
            
            // Use the fundamental entry method, passing through display_name
            return await this.enterByChannelId(channel.id, channel, display_name);
        } catch (error) {
            this.onError?.('enterByRoomCode', error);
            throw error;
        }
    }
    
    /**
     * Alias for backward compatibility
     * @deprecated Use enterByRoomCode() instead
     */
    async joinByRoomCode(roomCode, display_name = null) {
        return this.enterByRoomCode(roomCode, display_name);
    }
    
    /**
     * Alias for backward compatibility
     * @deprecated Use enterByChannelId() instead
     */
    async joinByChannelId(channelId, channelData = null, display_name = null) {
        return this.enterByChannelId(channelId, channelData, display_name);
    }
    
    /**
     * Try to resume the previous session from the vibelive_auth cookie (set by any
     * successful login/signup; 24h max-age). Returns the user on success, or null when
     * no valid stored session exists — never throws for a missing/expired token.
     * Note: the cookie is origin-wide, so the restored identity is whichever login or
     * anonymous signup last succeeded in this browser profile.
     */
    async restoreLogin() {
        this.#bridge = new Bridge();
        this.#setupGlobalCallbacks();
        const restoredUser = await this.#bridge.restoreSession();
        if (!restoredUser) return null;
        this.#currentUser = { ...restoredUser, userId: restoredUser.id, isRestored: true };
        Fetch.setUserId(restoredUser.id || restoredUser.pid);
        // Identity is finalized — start the WebSocket with the restored token
        this.#bridge.startDispatcher();
        await this.#bridge.protocolReady;
        this.onLogin?.(this.#currentUser);
        return this.#currentUser;
    }

    /**
     * Logout and cleanup
     */
    async logout() {
        try {
            // Leave channel if in one
            if (this.#inChannel) {
                this.stopLive();
            }
            
            // Graceful media shutdown (notifies peers, clears mute states)
            await this.#stopAllLocalMedia({ forceful: false });
            
            // Logout from bridge
            await this.#bridge?.logout({});
            
            // Reset channel + auth state
            this.#resetChannelState();
            this.#bridge = null;
            this.#currentUser = null;
            this.#channels = [];
            Fetch.clearUserId();
            Fetch.clearProxyUrl();
            
            this.onLogout?.();
        } catch (error) {
            this.onError?.('logout', error);
            console.error('Logout error:', error);
        }
    }
    
    // ========== CHANNELS ==========
    
    /**
     * Get channel information by channel ID
     * @param {string} channelId - The channel's database ID
     * @returns {Promise<Object>} Channel object with { id, pid, title, description, ... }
     */
    async getChannelById(channelId) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to get channel info. Call signup() or login() first.', { context: 'getChannelById' });
        }
        
        try {
            const rawChannel = await this.#bridge.getOneChannel({
                channel_id: channelId,
                depth: 1
            });
            // Normalize: strip members array, add memberCount
            // Apps should use getCurrentChannelMembers() for member data
            return this.#normalizeChannel(rawChannel);
        } catch (error) {
            this.onError?.('getChannelById', error);
            throw error;
        }
    }
    
    /**
     * Get channel information by room code (without joining).
     * Internal use — called by enterByRoomCode where the identifier is always a room code.
     * @param {string} roomCode - The room code
     * @returns {Promise<Object>} Channel object
     */
    async getChannelByRoomCode(roomCode) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to get channel info. Call signup() or login() first.', { context: 'getChannelByRoomCode' });
        }
        const rawChannel = await this.#bridge.getOneChannel({ room_code: roomCode, depth: 1 });
        return this.#normalizeChannel(rawChannel);
    }

    /**
     * Get channel info by any identifier, or the current channel if none provided.
     *
     * Resolution order:
     *   1. No identifier → returns the current channel (same as VibeLive.channel but freshly fetched)
     *   2. Identifier looks like a channelId → try channel_id lookup first
     *   3. If that 404s → retry the same value as a room code
     *
     * This means callers can pass VibeLive.channel?.id, a room code, or nothing at all.
     *
     * @param {string} [identifier] - channelId, room code, or omit for current channel
     * @returns {Promise<Object|null>} Channel object, or null if no channel is active and no identifier given
     */
    async getChannelInfo(identifier) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to get channel info. Call signup() or login() first.', { context: 'getChannelInfo' });
        }
        const target = identifier || this.#currentChannelId;
        if (!target) return null;

        // Try as channelId first
        try {
            const raw = await this.#bridge.getOneChannel({ channel_id: target, depth: 1 });
            return this.#normalizeChannel(raw);
        } catch (e) {
            if (e?.code !== ErrorCodes.ROOM_NOT_FOUND) throw e; // re-throw auth/network errors
            // Not found as channelId — fall back to room code
            const raw = await this.#bridge.getOneChannel({ room_code: target, depth: 1 });
            return this.#normalizeChannel(raw);
        }
    }

    /**
     * Update a room's built-in fields (owner-only): title, description, maxSize, allowsGuests.
     * Distinct from setRoomMetadata(): these are platform-level channel columns that appear
     * in room lists and console views; metadata is the app-defined blob.
     * @param {Object} options - { title?, description?, maxSize?, allowsGuests? }
     * @param {string} [channelId] - Defaults to the current channel
     * @returns {Promise<Object>} The updated channel view
     */
    async updateRoom(options = {}, channelId) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to update a room.', { context: 'updateRoom' });
        }
        const resolvedChannelId = channelId || this.#currentChannelId;
        if (!resolvedChannelId) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'No channel — pass a channelId or enter a room first.', { context: 'updateRoom' });
        }
        const config = {};
        if (options.title !== undefined) config.title = options.title;
        if (options.description !== undefined) config.description = options.description;
        if (options.maxSize !== undefined) config.max_size = String(options.maxSize);
        if (options.allowsGuests !== undefined) config.allows_guests = String(!!options.allowsGuests);
        const result = await this.#bridge.updateChannel({ channel_id: resolvedChannelId, config });
        if (result?.error) {
            throw new MakedoError(ErrorCodes.AUTH_FORBIDDEN, result.error, { context: 'updateRoom', retriable: false });
        }
        return result;
    }

    /**
     * Set (or clear) the app-defined metadata blob on a room. Owner-only — the server
     * rejects requests from anyone but the room owner. The blob is any JSON-serializable
     * value (room profile, schedule, policies…) and is returned, parsed, as `.metadata`
     * on the channel object from getChannelInfo() — readable by any member of the
     * project without joining the room.
     * @param {Object|null} metadata - JSON-serializable value; null clears the metadata
     * @param {string} [channelId] - The channel ID (defaults to the current channel;
     *                               pass explicitly to edit a room you have not entered)
     * @returns {Promise<Object>} { success: true } on success
     */
    async setRoomMetadata(metadata, channelId) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to set room metadata.', { context: 'setRoomMetadata' });
        }
        const resolvedChannelId = channelId || this.#currentChannelId;
        if (!resolvedChannelId) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'No channel — pass a channelId or enter a room first.', { context: 'setRoomMetadata' });
        }
        const blob = (metadata === null || metadata === undefined) ? '' : JSON.stringify(metadata);
        const result = await this.#bridge.setChannelMetadata({ channel_id: resolvedChannelId, metadata: blob });
        if (result?.error) {
            throw new MakedoError(ErrorCodes.AUTH_FORBIDDEN, result.error, { context: 'setRoomMetadata', retriable: false });
        }
        return result;
    }

    /**
     * Remove a member from the current room. Owner-only — the server rejects requests
     * from anyone but the room owner (or a sys-admin). Works for both LIVE and lobby
     * (PRE-LIVE) members: the target's SDK fires its `kicked` event and their registered
     * tiles are cleaned up; remaining members see the normal `remoteLeft` flow.
     * @param {string} memberId - The member to remove (member ID, not user ID)
     * @param {string} [message] - Optional message shown to the removed member
     * @returns {Promise<Object>} { success: true } on success
     */
    async kickMember(memberId, message = null) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to remove a member.', { context: 'kickMember' });
        }
        if (!memberId) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'memberId is required.', { context: 'kickMember' });
        }
        const result = await this.#bridge.kickMember({ member_id: memberId, message });
        if (result?.error) {
            throw new MakedoError(ErrorCodes.AUTH_FORBIDDEN, result.error, { context: 'kickMember', retriable: false });
        }
        return result;
    }

    /**
     * Fetch recent messages for a channel, or a private member conversation.
     * @param {string} channelId - The channel ID (required)
     * @param {string} [memberId] - Member ID for private messages (omit for channel-wide)
     * @param {number} [count=10] - Number of messages to return
     * @param {number} [page=0] - Page offset (0-based)
     * @returns {Promise<Array>} Array of message objects in reverse-chronological order
     */
    async getMessages({channelId, memberId = null, count = 10, page = 0} = {}) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to get messages.', { context: 'getMessages' });
        }
        const resolvedChannelId = channelId || this.#currentChannelId;
        return await this.#bridge.getMessages({ channelId: resolvedChannelId, memberId, count, page, depth: 1 });
    }

    /**
     * Retrieve the transcript for a channel's recorded session. Returns on-demand
     * (no polling). For per-session AI summaries, use getSessions() instead.
     * @param {string} [channelId] - The channel ID (defaults to the current channel)
     * @returns {Promise<Object>} { channelId, status, text, segments } — status: 'pending'|'ready'
     */
    async getTranscript(channelId) {
        return await Fetch.getTranscript(channelId || this.#currentChannelId);
    }

    /**
     * Retrieve the list of session summaries for a channel. Each session is one
     * member's continuous time in the room; `summary` is the AI-generated headline.
     * Visibility is requestor-aware on the server: hosts/admins see all sessions,
     * members see only sessions they participated in. Pair with the
     * 'sessionSummaryReady' event to know when a new summary is ready.
     * @param {string} [channelId] - The channel ID (defaults to the current channel)
     * @param {Object} [options]
     * @param {number} [options.depth=1] - 1 for summary fields, 2 to also include msvp_details
     * @param {number} [options.count] - max number of sessions to return
     * @returns {Promise<Array>} session objects: { sessionId, title, summary, tags, ... }
     */
    async getSessions(channelId, { depth = 1, count } = {}) {
        const list = await Fetch.getSessions(channelId || this.#currentChannelId, { depth, count });
        if (!Array.isArray(list)) return [];
        return list.map(s => ({ sessionId: s.pid, ...s }));
    }

    /** Alias for {@link getSessions}. */
    async getSummaries(channelId, options) {
        return this.getSessions(channelId, options);
    }

    /**
     * Send a text message to a channel or to a specific member.
     * @param {string} [channelId] - The channel ID (defaults to the current channel if omitted or null)
     * @param {string} [memberId] - Member ID for a private message (omit for channel-wide)
     * @param {string} content - The message text
     * @returns {Promise<Object>} The created message object
     */
    async sendMessage({channelId, memberId = null, content} = {}) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to send messages.', { context: 'sendMessage' });
        }
        const resolvedChannelId = channelId || this.#currentChannelId;
        return await this.#bridge.sendMessage({ channelId: resolvedChannelId, memberId, content });
    }

    /**
     * Soft-delete a single message. Only the message author may delete their own message.
     * @param {string} messageId - The message ID (`pid` field from a message object)
     * @returns {Promise<Object>} { success: true } on success
     */
    async deleteMessage(messageId) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to delete a message.', { context: 'deleteMessage' });
        }
        return await this.#bridge.deleteMessage({ messageId });
    }

    /**
     * Soft-delete all message history for a channel. Requires the caller to be the room
     * owner (creator) or a system admin.
     * @param {string} [channelId] - The channel ID (defaults to the current channel)
     * @param {Object} [options]
     * @param {boolean} [options.roomMessages=true]  - Delete channel-wide messages
     * @param {boolean} [options.dmMessages=false]   - Also delete member-to-member DMs in this room
     * @returns {Promise<Object>} { success: true } on success
     */
    async deleteTextHistory(channelId, { roomMessages = true, dmMessages = false } = {}) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to delete message history.', { context: 'deleteTextHistory' });
        }
        const resolvedChannelId = channelId || this.#currentChannelId;
        return await this.#bridge.deleteTextHistory({ channelId: resolvedChannelId, roomMessages, dmMessages });
    }

    // ==================== Availability & Invites (Phase A) ====================

    /** Mark self available within the current project. Transient/in-memory on server. */
    async setAvailable({ open = true, ttlSeconds = 3600, useProfile = '', seeking = '',
                          role = '', attributes = null } = {}) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to set availability.', { context: 'setAvailable' });
        }
        // Merge init({autoMatch:...}) defaults; per-call args win.
        const def = this.#autoMatchDefaults;
        const mergedRole       = role       || (def && def.role)       || '';
        const mergedAttributes = attributes || (def && def.attributes) || null;
        return await this.#bridge.setAvailable({
            open, ttlSeconds, useProfile, seeking,
            role: mergedRole, attributes: mergedAttributes,
        });
    }

    /** List users currently available in the caller's project. */
    async getAvailableUsers({ count = 20 } = {}) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to list available users.', { context: 'getAvailableUsers' });
        }
        return await this.#bridge.getAvailableUsers({ count });
    }

    /** Get pending invites for self. */
    async getInvites({ count = 10, depth = 1 } = {}) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to get invites.', { context: 'getInvites' });
        }
        return await this.#bridge.getInvites({ count, depth });
    }

    /** Send an invite for an existing channel. */
    async sendInvite({ channel_id, user_id, message = '', ttlSeconds = 0 } = {}) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to send invites.', { context: 'sendInvite' });
        }
        if (!channel_id || !user_id) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'sendInvite requires {channel_id, user_id}.', { context: 'sendInvite' });
        }
        return await this.#bridge.sendInvite({ channel_id, user_id, message, ttlSeconds });
    }

    /** Convenience: create a new room and pre-invite a list of users in one call.
     *  invites: [{ user_id, message? }, ...]
     */
    async createRoomWithInvites({ title = '', invites = [] } = {}) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to create a room.', { context: 'createRoomWithInvites' });
        }
        if (!Array.isArray(invites) || invites.length === 0) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'createRoomWithInvites requires a non-empty invites array.', { context: 'createRoomWithInvites' });
        }
        const room = await this.createChannel({ title });
        for (const inv of invites) {
            if (!inv?.user_id) continue;
            try {
                await this.#bridge.sendInvite({
                    channel_id: room.id || room.pid,
                    user_id: inv.user_id,
                    message: inv.message || '',
                    ttlSeconds: inv.ttlSeconds || 0
                });
            } catch (e) {
                console.warn(`createRoomWithInvites: failed to send invite to ${inv.user_id}:`, e);
            }
        }
        return room;
    }

    /** Accept an invite. Server flips status and returns channel info.
     *  If enterImmediately is true (default), SDK auto-enters by room_code.
     */
    async acceptInvite({ member_id, enterImmediately = true } = {}) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to accept invites.', { context: 'acceptInvite' });
        }
        if (!member_id) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'acceptInvite requires {member_id}.', { context: 'acceptInvite' });
        }
        const result = await this.#bridge.acceptInvite({ member_id });
        if (enterImmediately && result?.room_code) {
            await this.enterByRoomCode(result.room_code);
        }
        return result;
    }

    /** Decline an invite. */
    async declineInvite({ member_id } = {}) {
        if (!this.#currentUser) {
            throw new MakedoError(ErrorCodes.STATE_NOT_LOGGED_IN, 'Must be logged in to decline invites.', { context: 'declineInvite' });
        }
        if (!member_id) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'declineInvite requires {member_id}.', { context: 'declineInvite' });
        }
        return await this.#bridge.declineInvite({ member_id });
    }


    /**
     * Register a handler for real-time incoming text messages delivered over
     * the WebSocket. Fires whenever a `ThingMessage` is broadcast to the
     * logged-in user (channel/interval messages or private DMs). The author
     * does NOT receive an echo of their own send — for sender-side updates,
     * use the return value of sendMessage() directly.
     * @param {(message: Object, wsIncoming: Object) => void} callback
     *        message: { pid, owner_id, target_type, target_id, content, content_type, cts, uts, ... }
     */
    setOnMessage(callback) {
        if (typeof callback !== 'function') {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'setOnMessage requires a function', { context: 'setOnMessage' });
        }
        this.#bridge.setOnMessage(callback);
    }

    /**
     * Load available channels
     * @returns {Promise<Array>} List of channels
     */
    async loadChannels() {
        try {
            const rawChannels = await this.#bridge.getChannels({
                count: 20,
                depth: 1
            });
            
            // Normalize: strip members array, add memberCount
            // Apps should use getCurrentChannelMembers() for member data
            const channels = rawChannels.map(c => this.#normalizeChannel(c));
            
            this.#channels = channels;
            this.onChannelsLoaded?.(channels);
            return channels;
        } catch (error) {
            this.onError?.('loadChannels', error);
            throw error;
        }
    }
    
    /**
     * Select a channel from the cached channels list (puts you in PRE-LIVE state).
     * Requires loadChannels() to be called first.
     * Call startLive() after this to connect WebRTC.
     * @param {string} channelId 
     * @param {string} [display_name] - Optional display name to use for this channel (updates existing member or sets for new member)
     * @returns {Promise<Object>} The selected channel
     */
    async selectChannel(channelId, display_name = null) {
        const channel = this.#channels.find(c => c.id === channelId);
        if (!channel) {
            const error = new MakedoError(ErrorCodes.ROOM_NOT_FOUND, `Channel "${channelId}" not found in loaded channels. Call loadChannels() first, then use a channel ID from the result.`, { context: 'selectChannel' });
            this.onError?.('selectChannel', error);
            throw error;
        }
        
        try {
            // Use the fundamental entry method with cached channel data
            return await this.enterByChannelId(channelId, channel, display_name);
        } catch (error) {
            this.onError?.('selectChannel', error);
            throw error;
        }
    }
    
    // ========== USERS ==========
    
    /**
     * Load available users
     * @returns {Promise<Array>} List of users
     */
    async loadUsers() {
        try {
            const users = await this.#bridge.getUsers({
                query: '',
                count: 20,
                depth: 1
            });
            
            // Filter out self
            this.#users = users.filter(u => !u.is_me);
            this.onUsersLoaded?.(this.#users);
            return this.#users;
        } catch (error) {
            this.onError?.('loadUsers', error);
            throw error;
        }
    }
    
    /**
     * Select a user to start a quick chat channel with them.
     * Creates or retrieves existing 1:1 channel and puts you in PRE-LIVE state.
     * Call startLive() after this to connect WebRTC.
     * @param {string} userId - The user's pid
     * @returns {Promise<Object>} The channel created/retrieved
     */
    async selectUser(userId) {
        const user = this.#users.find(u => u.pid === userId);
        if (!user) {
            const error = new Error('User not found: ' + userId);
            this.onError?.('selectUser', error);
            throw error;
        }
        
        try {
            // Wait for protocol.js to load
            await this.#bridge.protocolReady;
            
            // Create or get existing quick chat channel with this user
            const channel = await this.#bridge.createQuickChatChannel({
                invited: userId,
                message: ''
            });
            
            // Use the fundamental entry method with the channel data
            // Note: Quick chat channels may use pid as the identifier
            const channelId = channel.id || channel.pid;
            return await this.enterByChannelId(channelId, channel);
        } catch (error) {
            this.onError?.('selectUser', error);
            throw error;
        }
    }
    
    /**
     * Go back from channel (cleanup without full logout)
     */
    async backToChannels() {
        if (this.#inChannel) {
            this.stopLive();
        }
        
        // Graceful media shutdown (notifies peers, clears mute states)
        await this.#stopAllLocalMedia({ forceful: false });
        
        // Reset channel state (preserves auth + bridge for rejoining)
        this.#resetChannelState();
        
        this.onLocalMemberMediaChange?.();
        this.#fireMemberStateChange(this.#localMemberId);
    }
    
    // ========== LOCAL MEDIA CONTROLS ==========
    
    /**
     * Set local audio to a specific state.
     * @param {boolean} on - true = start mic, false = stop mic
     */
    async setAudio(on) {
        if (on === this.#localMedia.audio) return; // Idempotent
        
        try {
            if (on) {
                const stream = await this.#bridge.createLocalMedia(
                    'camera',
                    true,
                    this.#localMedia.video
                );
                
                if (stream) {
                    this.#localMedia.audio = true;
                    this.#localMedia.audioMuted = false; // Fresh capture is never muted
                    BrowserActivity.connectLocalAudio(stream);
                    
                    // Update local video element
                    if (this.#localVideoElement && !this.#localVideoElement.srcObject) {
                        this.#localVideoElement.srcObject = stream;
                        this.#localVideoElement.play();
                    }
                    
                    // Broadcast if in channel
                    if (this.#inChannel) {
                        await this.#bridge.startLiveMedia('camera', 'audio');
                    }
                }
            } else {
                this.#localMedia.audio = false;
                BrowserActivity.disconnectLocalAudio();
                
                if (this.#localMedia.video) {
                    await this.#bridge.stopLocalMedia('camera', 'audio');
                    if (this.#inChannel) {
                        await this.#bridge.stopLiveMedia('camera', 'audio');
                    }
                } else {
                    await this.#bridge.stopLocalMedia('camera');
                    if (this.#localVideoElement) {
                        this.#localVideoElement.srcObject = null;
                    }
                }
            }
            
            this.onLocalMemberMediaChange?.();
            this.#fireMemberStateChange(this.#localMemberId);
        } catch (error) {
            this.onError?.('setAudio', error);
            throw error;
        }
    }
    
    /** Toggle local audio on/off. Prefer setAudio(on) for explicit state. */
    async toggleAudio() {
        return this.setAudio(!this.#localMedia.audio);
    }
    
    /**
     * Set local video to a specific state.
     * @param {boolean} on - true = start camera, false = stop camera
     */
    async setVideo(on) {
        if (on === this.#localMedia.video) return; // Idempotent
        
        try {
            if (on) {
                const stream = await this.#bridge.createLocalMedia(
                    'camera',
                    this.#localMedia.audio,
                    true
                );
                
                if (stream) {
                    this.#localMedia.video = true;
                    this.#localMedia.videoMuted = false; // Fresh capture is never muted
                    BrowserActivity.setLocalVideoActive(true);
                    // Reconnect audio analyser — createLocalMedia may return a new MediaStream
                    // object when adding video to an existing audio-only stream, detaching the
                    // old analyser source from the live tracks.
                    if (this.#localMedia.audio) BrowserActivity.connectLocalAudio(stream);
                    
                    // Monitor video track for external termination (permissions revoked, device unplugged)
                    const videoTrack = stream.getVideoTracks()[0];
                    if (videoTrack) {
                        videoTrack.onended = () => {
                            console.warn('⚠️ Camera video track ended externally');
                            this.#handleExternalTrackEnd('camera', 'video');
                        };
                    }
                    
                    if (this.#localVideoElement) {
                        this.#localVideoElement.srcObject = stream;
                        this.#localVideoElement.play();
                    } else {
                        const msg = 'Video stream created but no element registered via setLocalCamera(). The stream exists but won\'t be visible. Call setLocalCamera(element) to display it.';
                        console.warn('[VibeLive]', msg);
                        this.onWarning?.('setVideo', msg);
                    }
                    
                    if (this.#inChannel) {
                        await this.#bridge.startLiveMedia('camera', 'video');
                    }
                }
            } else {
                this.#localMedia.video = false;
                BrowserActivity.setLocalVideoActive(this.#screenshare.video);
                
                if (this.#localMedia.audio) {
                    await this.#bridge.stopLocalMedia('camera', 'video');
                    if (this.#inChannel) {
                        await this.#bridge.stopLiveMedia('camera', 'video');
                    }
                } else {
                    await this.#bridge.stopLocalMedia('camera');
                    if (this.#localVideoElement) {
                        this.#localVideoElement.srcObject = null;
                    }
                }
            }
            
            this.onLocalMemberMediaChange?.();
            this.#fireMemberStateChange(this.#localMemberId);
        } catch (error) {
            this.onError?.('setVideo', error);
            throw error;
        }
    }
    
    /** Toggle local video on/off. Prefer setVideo(on) for explicit state. */
    async toggleVideo() {
        return this.setVideo(!this.#localMedia.video);
    }
    
    /**
     * Switch to a specific camera device. Turns video on if not already.
     * @param {string} deviceId - MediaDeviceInfo.deviceId from getDevices()
     */
    async setCamera(deviceId) {
        if (!deviceId || typeof deviceId !== 'string') {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'setCamera requires a deviceId string. Use getDevices() to enumerate available cameras.', { context: 'setCamera' });
        }
        try {
            if (this.#localMedia.video) {
                // Hot-swap: replace the video track in-place without tearing down the stream
                // or the WebRTC sender. syncVideoTrackToClone() calls sender.replaceTrack()
                // on the existing peer connection — no re-negotiation, no frozen frame.
                await this.#bridge.replaceVideoTrack('camera', deviceId);

                // Re-attach onended handler to the new track
                const newStream = this.#bridge.getLocalStream('camera');
                const newVideoTrack = newStream?.getVideoTracks()[0];
                if (newVideoTrack) {
                    newVideoTrack.onended = () => {
                        console.warn('⚠️ Camera video track ended externally');
                        this.#handleExternalTrackEnd('camera', 'video');
                    };
                }
                // Reassign srcObject so the browser re-evaluates the updated track list
                if (this.#localVideoElement && newStream) {
                    this.#localVideoElement.srcObject = newStream;
                }
            } else {
                // Video not currently on — create a fresh stream targeting the new device
                const stream = await this.#bridge.createLocalMedia(
                    'camera',
                    this.#localMedia.audio,
                    true,
                    deviceId
                );
                if (stream) {
                    this.#localMedia.video = true;
                    this.#localMedia.videoMuted = false;
                    BrowserActivity.setLocalVideoActive(true);
                    if (this.#localMedia.audio) BrowserActivity.connectLocalAudio(stream);
                    const videoTrack = stream.getVideoTracks()[0];
                    if (videoTrack) {
                        videoTrack.onended = () => {
                            console.warn('⚠️ Camera video track ended externally');
                            this.#handleExternalTrackEnd('camera', 'video');
                        };
                    }
                    if (this.#localVideoElement) {
                        this.#localVideoElement.srcObject = stream;
                        this.#localVideoElement.play();
                    }
                    if (this.#inChannel) {
                        await this.#bridge.startLiveMedia('camera', 'video');
                    }
                }
            }
            this.onLocalMemberMediaChange?.();
            this.#fireMemberStateChange(this.#localMemberId);
        } catch (error) {
            this.onError?.('setCamera', error);
            throw error;
        }
    }

    /**
     * Switch to a specific microphone device. Turns audio on if not already.
     * @param {string} deviceId - MediaDeviceInfo.deviceId from getDevices()
     */
    async setMicrophone(deviceId) {
        if (!deviceId || typeof deviceId !== 'string') {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'setMicrophone requires a deviceId string. Use getDevices() to enumerate available microphones.', { context: 'setMicrophone' });
        }
        try {
            if (this.#localMedia.audio) {
                // Hot-swap: replace the audio track in-place without tearing down the stream
                // or the WebRTC sender. syncAudioTrackToClone() calls sender.replaceTrack()
                // on the existing peer connection — seamless device switch, no stream gap.
                await this.#bridge.replaceAudioTrack('camera', deviceId);
                // Reconnect the analyser to the updated stream (same object, new track)
                const stream = this.#bridge.getLocalStream('camera');
                if (stream) BrowserActivity.connectLocalAudio(stream);
            } else {
                // Audio not currently on — create a fresh stream targeting the new device
                const stream = await this.#bridge.createLocalMedia(
                    'camera',
                    true,
                    this.#localMedia.video,
                    deviceId
                );
                if (stream) {
                    this.#localMedia.audio = true;
                    this.#localMedia.audioMuted = false;
                    BrowserActivity.connectLocalAudio(stream);
                    if (this.#inChannel) {
                        await this.#bridge.startLiveMedia('camera', 'audio');
                    }
                }
            }
            this.onLocalMemberMediaChange?.();
            this.#fireMemberStateChange(this.#localMemberId);
        } catch (error) {
            this.onError?.('setMicrophone', error);
            throw error;
        }
    }

    /**
     * Enumerate available media devices (cameras, microphones, speakers).
     * @returns {Promise<{cameras: Array, microphones: Array, speakers: Array}>}
     */
    async getDevices() {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return {
            cameras: devices.filter(d => d.kind === 'videoinput'),
            microphones: devices.filter(d => d.kind === 'audioinput'),
            speakers: devices.filter(d => d.kind === 'audiooutput'),
        };
    }

    /**
     * Set audio muted state. Requires audio to be on.
     * @param {boolean} muted - true = mute, false = unmute
     */
    async setAudioMuted(muted) {
        if (!this.#localMedia.audio) return;
        if (muted === this.#localMedia.audioMuted) return; // Idempotent
        
        try {
            await this.#bridge.setMuteLiveMedia('camera', 'audio', muted);
            this.#localMedia.audioMuted = muted;
            
            this.onLocalMemberMediaChange?.();
            this.#fireMemberStateChange(this.#localMemberId);
        } catch (error) {
            this.onError?.('setAudioMuted', error);
            throw error;
        }
    }
    
    /** Toggle audio mute. Prefer setAudioMuted(muted) for explicit state. */
    async toggleMuteAudio() {
        return this.setAudioMuted(!this.#localMedia.audioMuted);
    }
    
    /**
     * Set video muted/hidden state. Requires video to be on.
     * @param {boolean} muted - true = hide video, false = show video
     */
    async setVideoMuted(muted) {
        if (!this.#localMedia.video) return;
        if (muted === this.#localMedia.videoMuted) return; // Idempotent
        
        try {
            await this.#bridge.setMuteLiveMedia('camera', 'video', muted);
            this.#localMedia.videoMuted = muted;
            
            this.onLocalMemberMediaChange?.();
            this.#fireMemberStateChange(this.#localMemberId);
        } catch (error) {
            this.onError?.('setVideoMuted', error);
            throw error;
        }
    }
    
    /** Toggle video mute/hide. Prefer setVideoMuted(muted) for explicit state. */
    async toggleMuteVideo() {
        return this.setVideoMuted(!this.#localMedia.videoMuted);
    }
    
    /**
     * Set screenshare to a specific state.
     * @param {boolean} on - true = start screenshare, false = stop screenshare
     * @returns {Promise<boolean>} The resulting screenshare state. When the user
     *   dismisses the OS screen-picker (or the browser denies the request), this
     *   resolves to `false` rather than throwing — the picker cancel is a normal
     *   user action, not an exceptional condition. Genuine errors (device gone,
     *   network failure, etc.) still throw.
     */
    async setScreenshare(on) {
        if (on === this.#screenshare.video) return this.#screenshare.video; // Idempotent
        
        try {
            if (on) {
                let stream;
                try {
                    stream = await this.#bridge.createLocalMedia(
                        'screenshare',
                        false,
                        true
                    );
                } catch (pickerErr) {
                    // The user cancelled the OS screen-picker (or the browser
                    // denied the request). Treat this as a no-op rather than
                    // surfacing a thrown exception to the app — it's a normal
                    // user choice, equivalent to never having clicked the
                    // toggle. State stays false; caller can re-invoke later.
                    const code = pickerErr?.code;
                    if (code === ErrorCodes.SCREENSHARE_CANCELLED ||
                        code === ErrorCodes.SCREENSHARE_PERMISSION_DENIED) {
                        return false;
                    }
                    throw pickerErr;
                }
                
                if (stream) {
                    this.#screenshare.video = true;
                    this.#screenshare.videoMuted = false; // Fresh capture is never muted
                    BrowserActivity.setLocalVideoActive(true);
                    if (this.#localScreenElement) {
                        this.#localScreenElement.srcObject = stream;
                        this.#localScreenElement.play();
                    } else {
                        const msg = 'Screenshare stream created but no element registered via setLocalScreen(). The stream exists but won\'t be visible. Call setLocalScreen(element) to display it.';
                        console.warn('[VibeLive]', msg);
                        this.onWarning?.('setScreenshare', msg);
                    }
                    
                    // Start live broadcast BEFORE attaching onended listener
                    // (tracks get cloned during broadcast, we monitor the source track)
                    if (this.#inChannel) {
                        await this.#bridge.startLiveMedia('screenshare', 'video');
                    }
                    
                    // Monitor screenshare track for external termination (OS "Stop Sharing" button)
                    const videoTrack = stream.getVideoTracks()[0];
                    if (videoTrack) {
                        videoTrack.onended = () => {
                            console.warn('⚠️ Screenshare track ended externally');
                            this.#handleExternalTrackEnd('screenshare', 'video');
                        };
                    }
                }
            } else {
                this.#screenshare.video = false;
                this.#screenshare.videoMuted = false; // Reset muted state on stop
                BrowserActivity.setLocalVideoActive(this.#localMedia.video);
                
                await this.#bridge.stopLocalMedia('screenshare');
                if (this.#localScreenElement) {
                    this.#localScreenElement.srcObject = null;
                }
                if (this.#inChannel) {
                    await this.#bridge.stopLiveMedia('screenshare', 'video');
                }
            }
            
            this.onLocalMemberMediaChange?.();
            this.#fireMemberStateChange(this.#localMemberId);
            return this.#screenshare.video;
        } catch (error) {
            this.onError?.('setScreenshare', error);
            throw error;
        }
    }
    
    /** Toggle screenshare on/off. Prefer setScreenshare(on) for explicit state. */
    async toggleScreenshare() {
        return this.setScreenshare(!this.#screenshare.video);
    }
    
    /**
     * Set screenshare muted/hidden state. Requires screenshare to be on and in channel.
     * @param {boolean} muted - true = hide screenshare, false = show screenshare
     */
    async setScreenshareMuted(muted) {
        if (!this.#screenshare.video || !this.#inChannel) return;
        if (muted === this.#screenshare.videoMuted) return; // Idempotent
        
        try {
            await this.#bridge.setMuteLiveMedia('screenshare', 'video', muted);
            this.#screenshare.videoMuted = muted;
            
            this.onLocalMemberMediaChange?.();
            this.#fireMemberStateChange(this.#localMemberId);
        } catch (error) {
            this.onError?.('setScreenshareMuted', error);
            throw error;
        }
    }
    
    /** Toggle screenshare mute/hide. Prefer setScreenshareMuted(muted) for explicit state. */
    async toggleMuteScreenshare() {
        return this.setScreenshareMuted(!this.#screenshare.videoMuted);
    }
    
    /**
     * Handle external track termination (OS stop button, permissions revoked, device unplugged)
     * Bypasses the broken event routing that blocks self events.
     * @private
     */
    #handleExternalTrackEnd(streamType, trackType) {
        console.debug(`🔧 Handling external track end: ${streamType} ${trackType}`);
        
        try {
            // Update local state based on stream and track type
            if (streamType === 'camera') {
                if (trackType === 'video') {
                    this.#localMedia.video = false;
                    this.#localMedia.videoMuted = false;
                    
                    // Clear video element
                    if (this.#localVideoElement) {
                        this.#localVideoElement.srcObject = null;
                    }
                } else if (trackType === 'audio') {
                    this.#localMedia.audio = false;
                    this.#localMedia.audioMuted = false;
                }
            } else if (streamType === 'screenshare') {
                this.#screenshare.video = false;
                this.#screenshare.videoMuted = false;
                
                // Clear screen element
                if (this.#localScreenElement) {
                    this.#localScreenElement.srcObject = null;
                }
            }
            
            // Notify Bridge and server about the state change
            // This ensures other clients are informed.
            //
            // For screenshare: the user clicked the browser's native "Stop sharing"
            // bar. That ends the local MediaStreamTrack but does NOT tear down the
            // Galene upstream PeerConnection — meaning the SFU still thinks we're
            // publishing and will keep re-advertising the stream to remote peers
            // (causing ghost screenshare tiles for both existing viewers and any
            // late-joiners). We must call stopLocalMedia() to teardown the PC and
            // send the Galene `close` frame, in addition to the per-track
            // stopLiveMedia() broadcast toggle.
            if (this.#inChannel) {
                if (streamType === 'screenshare') {
                    this.#bridge.stopLocalMedia('screenshare').catch(err => {
                        console.error('Error closing screenshare upstream on external end:', err);
                    });
                }
                this.#bridge.stopLiveMedia(streamType, trackType).catch(err => {
                    console.error('Error notifying server of external track end:', err);
                });
            }
            
            // Notify the app so UI can update
            this.onLocalMemberMediaChange?.();
            this.#fireMemberStateChange(this.#localMemberId);
            
            console.debug(`✓ Local state synchronized after external ${streamType} ${trackType} termination`);
        } catch (error) {
            console.error('Error handling external track end:', error);
            this.onError?.('handleExternalTrackEnd', error);
        }
    }
    
    // ========== CHANNEL JOIN/LEAVE ==========
    
    /**
     * Start live - connect WebRTC and start streaming.
     * Call this after enterByRoomCode() or enterByChannelId() to go from PRE-LIVE to LIVE.
     */
    async startLive() {
        if (!this.#selectedChannel) {
            throw new MakedoError(ErrorCodes.STATE_NOT_IN_ROOM, 'No room entered. Call enterByRoomCode() or enterByChannelId() first, then call startLive().', { context: 'startLive' });
        }
        
        try {
            await this.#bridge.joinServer();
        } catch (error) {
            this.onError?.('startLive', error);
            throw error;
        }
    }
    
    /**
     * Stop live - disconnects WebRTC but maintains channel membership.
     * You can see other members and quickly rejoin with startLive().
     * @param {boolean} keepLocalMedia - Whether to keep local media streams
     */
    stopLive(keepLocalMedia = true) {
        try {
            this.#bridge?.leaveServer(!keepLocalMedia);
            // Note: Keep #members populated - you're still observing the channel
        } catch (error) {
            this.onError?.('stopLive', error);
            throw error;
        }
    }
    
    /**
     * Acknowledge a server liveliness check — resets the zombie-detection clock.
     * Call this from within your onLivelinessCheck handler once the user confirms
     * they are still present (e.g. clicks "Yes, I'm here" in your UI).
     */
    ackLiveliness() {
        this.#bridge.sendLivelinessAck(this.#localMemberId);
    }

    /**
     * Exit channel with full teardown - stops all media streams directly.
     * This is more aggressive than leave() and ensures camera/mic are released.
     * Does NOT logout - user remains authenticated and can join another channel.
     */
    async exitChannel() {
        console.debug('VibeLiveCore: exitChannel - full teardown');
        
        try {
            // Auto-cleanup all registered tiles before teardown
            this.#removeAllRegisteredTiles();

            // Forceful media shutdown — track.stop() guarantees hardware release
            await this.#stopAllLocalMedia({ forceful: true });
            
            // Ensure the server knows we're leaving, even if we're already out of
            // the SFU (e.g. lobby_mid → steppedout).  leaveServer() is a no-op when
            // the SFU connection is gone, so we always fire a direct HTTP status
            // update as a fallback.  The server's setStatus() is idempotent — a
            // duplicate "steppedout" after leaveServer already set it is harmless.
            if (this.#localMemberId && this.#bridge) {
                this.#bridge.setMemberStatus(this.#localMemberId, 'steppedout');
                this.#bridge.delog('member', this.#localMemberId, 'member_left', {});
                this.#bridge.DBPing('member', this.#localMemberId);
            }

            // Tell bridge to leave and kill all streams
            this.#bridge?.leaveServer(true);
            
            // Capture our member id BEFORE #resetChannelState() nulls #localMemberId.
            // Otherwise the terminal #fireMemberStateChange() below receives null and
            // its `if (memberId)` guard drops the event entirely — leaving listeners
            // (e.g. media-button UI) with a stale pre-exit state.
            const exitingMemberId = this.#localMemberId;

            // Reset channel state (preserves auth + bridge for joining another channel)
            this.#resetChannelState();
            
            // Fire callbacks
            this.onLocalMemberMediaChange?.();
            this.onLocalMemberLeft?.();
            this.onLocalLiveChange?.(false);
            // Emits { status:'EXITED', live:false, video/audio/screenVideo:'OFF' } for self,
            // so apps can reset local UI off a single, correctly-identified event.
            this.#fireMemberStateChange(exitingMemberId);
            
            console.debug('VibeLiveCore: exitChannel complete');
        } catch (error) {
            console.error('VibeLiveCore: exitChannel error:', error);
            this.onError?.('exitChannel', error);
        }
    }

    // ========== PRIVATE METHODS ==========
    
    /**
     * Stop all local media streams and clear their video elements.
     * Two modes:
     *   - forceful (default): calls track.stop() directly to guarantee hardware release (camera light off)
     *   - graceful: delegates to Bridge.stopLocalMedia() which clears mute states and notifies peers
     * Also stops tracks on remote member video elements to prevent stale playback.
     * @private
     * @param {Object} [options]
     * @param {boolean} [options.forceful=true] - true = track.stop() for immediate hardware release;
     *                                           false = bridge.stopLocalMedia() for clean shutdown
     */
    async #stopAllLocalMedia({ forceful = true } = {}) {
        if (forceful) {
            // Direct track.stop() — guarantees camera/mic hardware is released
            if (this.#localVideoElement?.srcObject) {
                this.#localVideoElement.srcObject.getTracks().forEach(track => track.stop());
                this.#localVideoElement.srcObject = null;
            }
            if (this.#localScreenElement?.srcObject) {
                this.#localScreenElement.srcObject.getTracks().forEach(track => track.stop());
                this.#localScreenElement.srcObject = null;
            }
            // Also stop remote member tracks to prevent stale playback
            this.#members.forEach(member => {
                if (member.videoElements) {
                    ['camera', 'screenshare'].forEach(streamType => {
                        const videoEl = member.videoElements[streamType];
                        if (videoEl?.srcObject) {
                            videoEl.srcObject.getTracks().forEach(track => track.stop());
                            videoEl.srcObject = null;
                        }
                    });
                }
            });
        } else {
            // Delegate to Bridge — clears mute states and notifies peers cleanly
            if (this.#localMedia.audio || this.#localMedia.video) {
                await this.#bridge?.stopLocalMedia('camera');
            }
            if (this.#screenshare.video) {
                await this.#bridge?.stopLocalMedia('screenshare');
            }
            if (this.#localVideoElement) {
                this.#localVideoElement.srcObject = null;
            }
            if (this.#localScreenElement) {
                this.#localScreenElement.srcObject = null;
            }
        }
    }
    
    /**
     * Reset channel-related internal state back to defaults.
     * Used by exitChannel, backToChannels, and logout for consistent cleanup.
     * Does NOT touch auth state (#currentUser, #bridge, #channels) — callers manage those.
     * @private
     */
    #resetChannelState() {
        BrowserActivity.setMediaHeartbeatSender(null); // revert to HTTP on exit
        BrowserActivity.clearActiveChannel(this.#currentChannelId);
        this.#stopQualityMonitor();
        this.#inChannel = false;
        this.#selectedChannel = null;
        this.#currentChannelId = null;
        this.#localMemberId = null;
        this.#localMedia = { audio: false, video: false, audioMuted: false, videoMuted: false };
        this.#screenshare = { audio: false, video: false, videoMuted: false };
        this.#members.clear();
        this.#pendingMembers.clear();
        this.#tileRegistry.clear();
    }

    /** Start the optional connection-quality monitor. No-op when the feature flag is off. */
    #startQualityMonitor(localMemberId) {
        if (!this.#features.connectionQuality) return;
        if (this.#qualityMonitor) return;
        this.#qualityMonitor = createQualityMonitor({
            getPeerConnections: () => this.#bridge?.getAllActivePeerConnections() || [],
            getKnownMemberIds: () => [...this.#tileRegistry.keys()].filter(id => id !== this.#localMemberId),
            localMemberId,
            thresholds: this.#qualityThresholds || undefined,
            onLevelChange: (snapshot) => {
                // Overall snapshot (folds upstream + worst remote downstream). Used only
                // for the public onConnectionQuality callback — it must NOT drive the
                // local self-tile banner, because a single flaky remote downstream would
                // otherwise blame the local camera for someone else's network.
                try { this.onConnectionQuality?.(snapshot); }
                catch (e) { console.debug('[VibeLiveCore] onConnectionQuality threw:', e); }
            },
            onLocalLevelChange: (snapshot) => {
                // Upstream-only snapshot — reflects strictly your outbound link to the
                // SFU. This is the right signal for the self-tile banner.
                if (this.#features.showLocalQuality) {
                    try { this.#renderQualityBar(this.#localMemberId, snapshot, /*isLocal*/true); }
                    catch (e) { console.debug('[VibeLiveCore] local quality bar render failed:', e); }
                }
            },
            onMemberLevelChange: (memberId, snapshot) => {
                if (this.#features.showMemberQuality) {
                    try { this.#renderQualityBar(memberId, snapshot, /*isLocal*/false); }
                    catch (e) { console.debug('[VibeLiveCore] member quality bar render failed:', e); }
                }
                try { this.onMemberQuality?.(memberId, snapshot); }
                catch (e) { console.debug('[VibeLiveCore] onMemberQuality threw:', e); }
            },
        });
        this.#qualityMonitor.start();

        // Wire native offline/online so the monitor fires immediately on network loss
        // rather than waiting for the stall threshold (~8 s).
        this.#qualityOfflineHandler = () => this.#qualityMonitor?.signalOffline();
        this.#qualityOnlineHandler  = () => this.#qualityMonitor?.signalOnline();
        window.addEventListener('offline', this.#qualityOfflineHandler);
        window.addEventListener('online',  this.#qualityOnlineHandler);
    }

    #stopQualityMonitor() {
        if (this.#qualityOfflineHandler) {
            window.removeEventListener('offline', this.#qualityOfflineHandler);
            window.removeEventListener('online',  this.#qualityOnlineHandler);
            this.#qualityOfflineHandler = null;
            this.#qualityOnlineHandler  = null;
        }
        if (this.#qualityMonitor) {
            this.#qualityMonitor.stop();
            this.#qualityMonitor = null;
        }
    }

    // ─────────────────────────────────────────────────────────────────────
    // Quality-bar overlay (optional UX)
    // ─────────────────────────────────────────────────────────────────────
    // Renders / updates / removes a top-edge status bar on a member's camera tile.
    // Only invoked when features.showMemberQuality (remote) or features.showLocalQuality
    // (self) is enabled. Driven entirely off snapshot.level + snapshot.reason; on level
    // 'good' the bar is removed.
    #renderQualityBar(memberId, snapshot, isLocal) {
        if (!memberId || !snapshot) return;
        const entry = this.#tileRegistry.get(memberId);
        const tile = entry?.camera;
        if (!tile) return; // tile not registered (yet) — nothing to decorate

        const level = snapshot.level;
        let bar = tile.querySelector(':scope > .makedo-quality-bar');

        if (level === 'good') {
            if (bar) bar.remove();
            return;
        }

        const cs = getComputedStyle(tile);
        if (cs.position === 'static') tile.style.position = 'relative';

        const palette = {
            fair: { bg: 'rgba(234,179,8,0.92)',  fg: '#1a1a1a' }, // amber
            poor: { bg: 'rgba(234,88,12,0.92)',  fg: '#ffffff' }, // orange
            lost: { bg: 'rgba(220,38,38,0.95)',  fg: '#ffffff' }, // red
        };
        const colors = palette[level] || palette.fair;
        const text = this.#qualityBarText(snapshot, isLocal);

        if (!bar) {
            bar = document.createElement('div');
            bar.className = 'makedo-quality-bar';
            bar.style.cssText = [
                'position:absolute',
                'top:0','left:0','right:0',
                'padding:4px 8px',
                'font:600 11px/1.3 system-ui,sans-serif',
                'text-align:center',
                'letter-spacing:.2px',
                'pointer-events:none',
                'z-index:20',
                'box-shadow:0 1px 2px rgba(0,0,0,.25)',
            ].join(';') + ';';
            tile.appendChild(bar);
        }
        bar.style.background = colors.bg;
        bar.style.color = colors.fg;
        bar.setAttribute('data-quality-level', level);
        bar.setAttribute('data-quality-reason', snapshot.reason || '');
        bar.textContent = text;
    }

    /** Map a snapshot's level/reason to a short user-facing string. @private */
    #qualityBarText(snapshot, isLocal) {
        const r = snapshot.reason;
        const lvl = snapshot.level;
        // --- SIMPLIFIED USER MESSAGES ---
        // To restore more detailed messages, uncomment the block below and remove the simplified returns.
        /*
        if (lvl === 'lost') {
            if (r === 'stalled') return 'No data — reconnecting…';
            if (r === 'offline') return 'Network unavailable';
            if (r === 'rtt')     return 'Connection lost';
            return 'Connection lost';
        }
        switch (r) {
            case 'audio-loss':    return isLocal ? 'Your audio is breaking up' : 'Audio is breaking up';
            case 'video-loss':    return isLocal ? 'Your video is degraded'    : 'Video is degraded';
            case 'video-freeze':  return isLocal ? 'Your video is freezing'    : 'Video is freezing';
            case 'jitter':        return 'Connection jitter';
            case 'rtt':           return 'High latency';
            case 'upstream-loss': return isLocal ? 'Upload issues'             : 'Connection unstable';
            case 'bitrate-low':   return 'Low bandwidth';
            default:              return isLocal ? 'Your connection is unstable' : 'Connection unstable';
        }
        */
        // --- SIMPLIFIED ---
        if (r === 'audio-loss') return 'Audio is degraded';
        if (r === 'video-loss' || r === 'video-freeze') return 'Video is degraded';
        if (lvl === 'lost') return 'Connection lost';
        return 'Connection unstable';
    }

    /**
     * Re-apply the current quality bar (if any) when a tile is (re)registered
     * after a quality change has already fired. @private
     */
    #applyCurrentQualityBar(memberId) {
        if (!this.#qualityMonitor) return;
        const isLocal = memberId === this.#localMemberId;
        if (isLocal) {
            if (!this.#features.showLocalQuality) return;
            // Use the upstream-only snapshot so a re-attaching tile doesn't pick up
            // "High latency" attributable to a remote peer's downstream.
            const snap = this.#qualityMonitor.getLocalSnapshot
                ? this.#qualityMonitor.getLocalSnapshot()
                : this.#qualityMonitor.getSnapshot();
            if (snap) this.#renderQualityBar(memberId, snap, true);
        } else {
            if (!this.#features.showMemberQuality) return;
            const snap = this.#qualityMonitor.getMemberSnapshot(memberId);
            if (snap) this.#renderQualityBar(memberId, snap, false);
        }
    }

    /** Current overall (your-side) network-quality snapshot, or null if monitor not running. */
    get connectionQuality() {
        return this.#qualityMonitor ? this.#qualityMonitor.getSnapshot() : null;
    }

    /** Per-member network-quality snapshot for a remote member, or null. */
    getMemberQuality(memberId) {
        return this.#qualityMonitor ? this.#qualityMonitor.getMemberSnapshot(memberId) : null;
    }

    /** Count remote members currently sending video (camera or screenshare). */
    #getRemoteVideoCount() {
        let count = 0;
        for (const [memberId, member] of this.#members) {
            if (memberId !== this.#localMemberId && (member.hasCamera || member.hasScreenshare)) count++;
        }
        return count;
    }
    
    /**
     * Ensure a member exists in the Map, fetching if needed.
     * Returns member data or null if fetch fails.
     * @private
     */
    /**
     * Seed bridge.membersMap mute flags from a server-fetched member object.
     * The server now returns camAudioMuted / camVideoMuted / screenVideoMuted
     * (in-memory fields on ThingMember, not DB-backed). This is the authoritative
     * source for late-joining peers — no peer re-announcement needed.
     * Safe to call with any member data; silently ignores missing fields.
     * @param {string} memberId
     * @param {Object} serverMemberData - Raw object from getOneMember / getMembers
     */
    #seedMuteStateFromServerData(memberId, serverMemberData) {
        if (!memberId || !serverMemberData) return;
        // Always update the member entry so #handleMemberStreamStart can apply it
        // to bridge.membersMap once the stream (and its membersMap entry) arrives.
        const memberEntry = this.#members.get(memberId);
        if (memberEntry) {
            memberEntry.camAudioMuted    = serverMemberData.camAudioMuted    === true;
            memberEntry.camVideoMuted    = serverMemberData.camVideoMuted    === true;
            memberEntry.screenVideoMuted = serverMemberData.screenVideoMuted === true;
        }
        // Also apply immediately if bridge.membersMap entry already exists
        // (handles the case where stream arrived before the HTTP member fetch returned).
        const mapEntry = this.#bridge.membersMap[memberId];
        if (mapEntry) {
            mapEntry.camera_audio_muted      = serverMemberData.camAudioMuted    === true;
            mapEntry.camera_video_muted      = serverMemberData.camVideoMuted    === true;
            mapEntry.screenshare_video_muted = serverMemberData.screenVideoMuted === true;
        }
    }

    async #ensureMember(memberId, depth = 1) {
        // Already in Map - return it
        let member = this.#members.get(memberId);
        if (member) {
            return member;
        }

        // trackMembers:false — skip the per-member getOneMember fetch (the O(N²) storm in large
        // rooms) and register a minimal entry so the member is still "known" (displayName defaults
        // to 'Unknown'; the app opted out of member data). Events still fire normally.
        if (!this.#trackMembers) {
            member = this.#createMemberEntry({});
            this.#members.set(memberId, member);
            return member;
        }

        // Check if another call is already fetching this member
        if (this.#pendingMembers.has(memberId)) {
            // Wait a bit and try again
            await new Promise(resolve => setTimeout(resolve, 100));
            return this.#members.get(memberId) || null;
        }
        
        // Fetch member data
        this.#pendingMembers.add(memberId);
        try {
            const memberData = await this.#bridge.getOneMember({
                member_id: memberId,
                depth
            });

            // Create member entry — merging tile/element state if a placeholder entry
            // appeared while the fetch was in flight (e.g. registerTile during the await).
            const existing = this.#members.get(memberId);
            member = this.#createMemberEntry(memberData);
            if (existing) {
                member.videoElements = existing.videoElements;
                member.hasCamera = existing.hasCamera;
                member.hasScreenshare = existing.hasScreenshare;
            }
            this.#members.set(memberId, member);
            // Seed mute state from server data into bridge.membersMap if entry already exists
            this.#seedMuteStateFromServerData(memberId, memberData);
            return member;
        } catch (error) {
            console.warn(`Could not fetch member ${memberId}:`, error);
            return null;
        } finally {
            this.#pendingMembers.delete(memberId);
        }
    }
    
    /** Wires bridge callbacks that must be active globally (not per-channel),
     *  e.g. invites, which can arrive before the user enters any channel. */
    #setupGlobalCallbacks() {
        if (!this.#bridge) return;
        // Re-apply the app's default subscription onto the freshly created bridge (bridge is
        // recreated per login/session); the bridge persists it across per-channel comms.
        if (this.#defaultSubscription) { this.#bridge.setDefaultSubscription(this.#defaultSubscription); }
        this.#bridge.setOnInviteUpdate((data) => {
            this.onInviteReceived?.(data);
        });
        this.#bridge.setOnInviteResponse((payload) => {
            this.onInviteResponse?.(payload);
        });
        this.#bridge.setOnAvailabilityUpdate((payload) => {
            this.onAvailabilityUpdate?.(payload);
        });
        this.#bridge.setOnTranscriptionState((channelId, state) => {
            this.onTranscriptionStateChange?.({ channelId, state });
        });
        this.#bridge.setOnSessionSummaryReady((channelId, sessionId) => {
            this.onSessionSummaryReady?.({ channelId, sessionId });
        });
    }

    #setupChannelCallbacks() {
        // Called when we successfully join
        this.#bridge.setOnSelfJoined(async () => {
            console.debug('✓ Joined channel');
            this.#inChannel = true;
            
            // Auto-broadcast enabled media
            if (this.#localMedia.audio) {
                this.#bridge.startLiveMedia('camera', 'audio');
            }
            if (this.#localMedia.video) {
                this.#bridge.startLiveMedia('camera', 'video');
            }
            if (this.#screenshare.video) {
                this.#bridge.startLiveMedia('screenshare', 'video');
            }

            // Declare current mute state for each active track on (re)join.
            // We always broadcast both directions (muted AND unmuted) — not just
            // 'muted' — because the server keeps mute state in-memory and does
            // not auto-clear it on member exit. If this member was muted in a
            // previous session, the server (and every peer's cache) still
            // believes they are muted; we must explicitly assert the current
            // state so peers see a correct indicator after rejoin.
            if (this.#localMedia.audio) {
                await this.#bridge.setMuteLiveMedia('camera', 'audio', this.#localMedia.audioMuted === true);
            }
            if (this.#localMedia.video) {
                await this.#bridge.setMuteLiveMedia('camera', 'video', this.#localMedia.videoMuted === true);
            }
            if (this.#screenshare.video) {
                await this.#bridge.setMuteLiveMedia('screenshare', 'video', this.#screenshare.videoMuted === true);
            }
            
            this.onLocalMemberJoined?.();
            this.onLocalLiveChange?.(true);
            this.#fireMemberStateChange(this.#localMemberId);
        });
        
        // Called when the server forcibly removes this user (e.g. meeting time limit).
        // Bridge has already stopped all tracks and cleaned up member/stream state.
        // Clear local media flags here (not in setOnSelfExited) because this is the
        // only exit path where streams were killed externally without exitChannel() or
        // stopLive() running first.
        this.#bridge.setOnKicked((message) => {
            // Auto-cleanup all registered tiles
            this.#removeAllRegisteredTiles();

            this.#localMedia.audio      = false;
            this.#localMedia.video      = false;
            this.#localMedia.audioMuted = false;
            this.#localMedia.videoMuted = false;
            this.#screenshare.audio      = false;
            this.#screenshare.video      = false;
            this.#screenshare.videoMuted = false;
            BrowserActivity.disconnectLocalAudio();
            BrowserActivity.setLocalVideoActive(false);
            this.onLocalMemberMediaChange?.();
            this.onLocalLiveChange?.(false);
            this.#fireMemberStateChange(this.#localMemberId);
            this.onKickedExt?.(message);
        });

        // Called when server sends a liveliness check (heartbeat went silent).
        this.#bridge.setOnLivelinessCheck(() => {
            this.onLivelinessCheck?.();
        });

        // Called when the server gives advance notice of an upcoming kick (non-destructive).
        this.#bridge.setOnKickWarning((warning) => {
            this.onKickWarning?.(warning);
        });

        // Invite callbacks are wired in #setupGlobalCallbacks() at bridge creation
        // time, since invites can arrive before the user ever enters a channel.

        // Called when we exit (voluntary stopLive or kicked via leaveServer).
        // Single responsibility: signal PRE-LIVE state and clear the beforeunload guard.
        // Do NOT clear #localMedia here — stopLive() keeps streams alive intentionally,
        // and kicked has its own setOnKicked callback for media cleanup.
        // exitChannel() calls #resetChannelState() synchronously before this fires,
        // so wasLive is already false on that path and we early-return.
        this.#bridge.setOnSelfExited(() => {
            console.debug('✓ Exited channel');
            const wasLive = this.#inChannel;
            this.#inChannel = false;

            if (!wasLive) {
                // Not live — fires from Galene's socket.onclose cleanup of a previous
                // connection inside serverConnect(), or exitChannel() which already reset
                // state synchronously.  Nothing to do.
                return;
            }

            // Genuine exit from a live session -- remove the beforeunload guard and
            // signal the PRE-LIVE state transition to the app.
            BrowserActivity.clearActiveChannel(this.#currentChannelId);
            this.onLocalMemberLeft?.();
            this.onLocalLiveChange?.(false);
            this.#fireMemberStateChange(this.#localMemberId);
        });
        
        // Called when member state changes
        this.#bridge.setOnMemberStateChange((memberId, event, streamType, data) => {
            const isSelf = memberId === this.#localMemberId;

            // Allow member_joined/left/stream_end/error for self, block others (avoids duplicate media_change events)
            // member_stream_end is needed as fallback when track.onended doesn't fire due to cloning
            if (isSelf && event !== 'member_joined' && event !== 'member_left' && event !== 'member_stream_end' && event !== 'error') {
                return;
            }

            switch (event) {
                case 'member_joined':      this.#handleMemberJoined(memberId);             break;
                case 'member_left':        this.#handleMemberLeft(memberId);               break;
                case 'member_stream_start': this.#handleMemberStreamStart(memberId, streamType); break;
                case 'member_stream_end':  this.#handleMemberStreamEnd(memberId, streamType);   break;
                case 'member_media_change': this.#handleMemberMediaChange(memberId, streamType); break;
                case 'error':              this.#handleAsyncError(data);                   break;
            }
        });
        
        // Called when member config is updated
        this.#bridge.setOnMemberUpdate(async (wsIncoming) => {
            const member_id = wsIncoming.member_id || wsIncoming.data?.id;
            if (!member_id) return;
            
            const isSelf = member_id === this.#localMemberId;
            
            // Check if we know about this member
            let member = this.#members.get(member_id);
            
            if (!member) {
                if (!this.#trackMembers) {
                    // trackMembers:false — build a minimal entry from the WS presence payload
                    // (no getOneMember REST fetch, so no O(N²) storm). Names/status come from the
                    // message if present; otherwise default.
                    member = this.#createMemberEntry({
                        display_name: wsIncoming.delta?.display_name || wsIncoming.data?.display_name,
                        status:       wsIncoming.delta?.status       || wsIncoming.data?.status,
                    });
                    const existing = this.#members.get(member_id);
                    if (existing) {
                        member.videoElements = existing.videoElements;
                        member.hasCamera = existing.hasCamera;
                        member.hasScreenshare = existing.hasScreenshare;
                    }
                    this.#members.set(member_id, member);
                } else {
                    // New member we haven't seen before - fetch their info
                    try {
                        const memberData = await this.#bridge.getOneMember({
                            member_id: member_id,
                            depth: 2
                        });

                        // Merge tile/element state if an entry appeared during the await
                        // (e.g. registerTile created a placeholder) — never replace it.
                        const existing = this.#members.get(member_id);
                        member = this.#createMemberEntry(memberData);
                        if (existing) {
                            member.videoElements = existing.videoElements;
                            member.hasCamera = existing.hasCamera;
                            member.hasScreenshare = existing.hasScreenshare;
                        }
                        this.#members.set(member_id, member);
                    } catch (error) {
                        console.error('Error fetching member data:', error);
                        return;
                    }
                }
            } else {
                // Update existing member status if provided
                if (wsIncoming.delta?.status) {
                    member.baseStatus = wsIncoming.delta.status;
                }
                // Update existing member display name if provided
                if (wsIncoming.delta?.display_name) {
                    member.displayName = wsIncoming.delta.display_name;
                }
            }
            
            // Auto-cleanup registered tiles when member has transitioned to EXITED.
            // Handles the race where #handleMemberLeft ran before the server had
            // processed the departure (status was still live), and this later
            // memberUpdate delivers the terminal status.
            if (!isSelf && this.#computeDisplayStatus(member.baseStatus) === 'EXITED') {
                this.#removeAllTiles(member_id);
            }

            // Notify app about the update
            this.onMemberUpdate?.(member_id);
            
            // Skip self for remote media change - self gets onLocalMemberMediaChange instead
            if (isSelf) return;
            
            // Also emit media change for existing tiles (remote members only)
            if (member.videoElements?.camera) {
                this.onRemoteMemberMediaChange?.(member_id, 'camera');
            }
            if (member.videoElements?.screenshare) {
                this.onRemoteMemberMediaChange?.(member_id, 'screenshare');
            }
            this.#fireMemberStateChange(member_id);
        });
    }
    
    async #handleMemberJoined(memberId) {
        const isSelf = memberId === this.#localMemberId;
        
        // Check if member already exists (PRE-LIVE → ACTIVE transition,
        // a previously-exited member rejoining, or an invited-but-not-yet-arrived
        // member who was pre-populated into #members by the enterByChannelId fetch).
        const existingMember = this.#members.get(memberId);
        if (existingMember) {
            // Capture status BEFORE updating — we need it to decide whether to fire
            // remoteJoined below. 'invited', 'steppedout', and any other EXITED-class
            // status means this is a genuine first-arrival and remoteJoined must fire.
            // PRE-LIVE or LIVE statuses mean the member was already present in the room
            // and we are just seeing a state transition — no duplicate remoteJoined.
            const wasExited = this.#computeDisplayStatus(existingMember.baseStatus) === 'EXITED';
            existingMember.baseStatus = 'nowinside';

            // Refresh mute state from the server on rejoin. Without this, stale
            // mute flags from the previous session leak into the new session and
            // get re-applied to bridge.membersMap by #handleMemberStreamStart,
            // causing peers to render the wrong indicator. Skip for self.
            // Skipped when trackMembers:false (no per-member fetches).
            if (!isSelf && this.#trackMembers) {
                try {
                    const memberData = await this.#bridge.getOneMember({ member_id: memberId, depth: 1 });
                    if (memberData) {
                        // Update displayName from fresh server data — the pre-populated entry
                        // from enterByChannelId may have 'Unknown' if the partner hadn't set
                        // their name yet when the room was fetched (common in invite flows).
                        if (memberData.display_name) {
                            existingMember.displayName = memberData.display_name;
                        }
                        this.#seedMuteStateFromServerData(memberId, memberData);
                    }
                } catch (error) {
                    console.warn(`Could not refresh member data for rejoining member ${memberId}:`, error);
                }
            }

            this.onMemberUpdate?.(memberId);
            // Fire remoteJoined for members who were invited/exited but not yet present.
            // This was previously suppressed for ALL existing members, causing remoteJoined
            // to silently fail in invite/pairing flows where enterByChannelId pre-populates
            // invited ThingMember records into #members before they actually arrive.
            if (!isSelf && wasExited) {
                this.onRemoteMemberJoined?.(memberId);
            }
            return;
        }
        
        // Don't create new entry for self (you get onLocalMemberJoined callback instead)
        if (isSelf) return;
        
        // New member joining - ensure they're in Map
        const member = await this.#ensureMember(memberId, 2);
        if (member) {
            member.baseStatus = 'nowinside';
            this.onRemoteMemberJoined?.(memberId);
        }
    }
    
    async #handleMemberLeft(memberId) {
        const isSelf = memberId === this.#localMemberId;

        // Ensure member is in Map (handles race condition if they joined during our join)
        const member = await this.#ensureMember(memberId);
        if (!member) {
            console.warn(`Member ${memberId} left but could not be found`);
            return;
        }

        // Apply bridge's immediately-known status as a preliminary baseline BEFORE the
        // server fetch.  Bridge has already set membersMap[memberId].status = 'steppedout'
        // synchronously during the kick sequence — this is correct ground truth.
        // The server fetch below may race and return a stale live status (e.g. 'nowinside')
        // if the server DB hasn't finished processing the kick by the time we fetch.
        const LIVE_STATUSES = new Set(['nowinside', 'lobby_pre', 'lobby_mid']);
        const bridgeStatus = this.#bridge.membersMap[memberId]?.status;
        if (bridgeStatus) {
            member.baseStatus = bridgeStatus;
        }

        // Refine with a server fetch for the precise terminal label (e.g. 'guest_out').
        // Guard: never let a live server status overwrite a terminal status already set
        // from bridge — that is a stale-read race condition.
        // Skipped when trackMembers:false — the bridge status set above is used as-is.
        if (this.#trackMembers) try {
            const memberData = await this.#bridge.getOneMember({ member_id: memberId, depth: 1 });
            if (memberData?.status) {
                const serverStatus      = memberData.status;
                const currentIsTerminal = !LIVE_STATUSES.has(member.baseStatus);
                const serverIsLive      = LIVE_STATUSES.has(serverStatus);
                if (!(currentIsTerminal && serverIsLive)) {
                    member.baseStatus = serverStatus;
                }
                // else: server is stale — keep the terminal status from bridge
            }
        } catch (error) {
            console.warn('Could not fetch updated member status after leave:', error);
        }

        // Clear media flags
        member.hasCamera = false;
        member.hasScreenshare = false;

        // Clear video elements
        this.#updateRemoteVideoVisibility(memberId, 'camera');
        this.#updateRemoteVideoVisibility(memberId, 'screenshare');

        // Notify app about status update
        this.onMemberUpdate?.(memberId);

        // Skip self — self gets onLocalMemberLeft via setOnSelfExited instead
        if (isSelf) return;

        // Auto-cleanup registered tiles when member has fully exited
        if (this.#computeDisplayStatus(member.baseStatus) === 'EXITED') {
            this.#removeAllTiles(memberId);
        }

        // Notify app that remote member left LIVE state.
        // App should check member.displayStatus to decide tile visibility.
        this.onRemoteMemberLeft?.(memberId);
        this.onRemoteMemberLiveChange?.(memberId, false);
        this.#fireMemberStateChange(memberId);
    }
    
    async #handleMemberStreamStart(memberId, streamType) {
        // Note: Stream may not exist yet (WebSocket state event can arrive before WebRTC stream)
        // We still create the tile — #updateRemoteVideoVisibility will attach the stream when available
        
        // Ensure member is in Map
        const member = await this.#ensureMember(memberId, 2);
        if (!member) {
            console.warn(`Stream started for unknown member ${memberId}`);
            return;
        }

        // Apply stored mute state to bridge.membersMap now that both are guaranteed to exist.
        // bridge.js creates the membersMap entry synchronously before firing this callback,
        // and #ensureMember has now resolved the member entry (from cache or fresh fetch).
        // This handles the common case where getCurrentChannelMembers() populated the member
        // entry before the stream arrived, so #ensureMember returned from cache without seeding.
        const mapEntry = this.#bridge.membersMap[memberId];
        if (mapEntry) {
            mapEntry.camera_audio_muted      = member.camAudioMuted    === true;
            mapEntry.camera_video_muted      = member.camVideoMuted    === true;
            mapEntry.screenshare_video_muted = member.screenVideoMuted === true;
        }

        // Update media flags
        if (streamType === 'camera') {
            if (member.hasCamera) {
                // Benign: streams.js fans out ondownstream + per-track events, so this
                // handler is re-entered 2-3× per logical stream. State writes are idempotent.
                // Demoted from onWarning to debug to avoid spurious app-level warnings.
                console.debug(`[VibeLive] Repeat stream_start for member ${memberId} camera (benign fan-out from streams.js).`);
            }
            member.hasCamera = true;
        } else if (streamType === 'screenshare') {
            if (member.hasScreenshare) {
                console.debug(`[VibeLive] Repeat stream_start for member ${memberId} screenshare (benign fan-out from streams.js).`);
            }
            member.hasScreenshare = true;
        }
        
        // Connect remote audio to monitoring analyser (camera streams carry audio)
        if (streamType === 'camera') {
            const stream = this.#bridge.getMemberMediaStream(memberId, 'camera');
            if (stream) BrowserActivity.connectRemoteAudio(memberId, stream);
        }
        BrowserActivity.setRemoteVideoCount(this.#getRemoteVideoCount());
        
        // Use unified visibility helper - handles case where stream starts but video is muted
        this.#updateRemoteVideoVisibility(memberId, streamType);
        
        this.onRemoteMemberStreamStart?.(memberId, streamType);
        if (streamType === 'camera') {
            this.onRemoteMemberLiveChange?.(memberId, true);
        }
        this.#fireMemberStateChange(memberId);
    }
    
    #handleMemberStreamEnd(memberId, streamType) {
        const isSelf = memberId === this.#localMemberId;
        const member = this.#members.get(memberId);
        if (!member) return;
        
        // Handle self stream end (fallback when track.onended doesn't fire)
        if (isSelf) {
            // Check if source track is still alive to distinguish:
            // - stopLive() called = track still 'live' → keep local preview
            // - OS stop button or external termination = track 'ended' → clear local preview
            
            if (streamType === 'screenshare') {
                const sourceTrack = this.#localScreenElement?.srcObject?.getVideoTracks()[0];
                
                if (sourceTrack && sourceTrack.readyState === 'live') {
                    console.debug('📺 Screenshare broadcast stopped but track still live → KEEPING local preview');
                    return; // Don't clear local state
                }
                
                // Track actually ended (OS stop button, permissions revoked, etc.)
                console.debug('🔄 Screenshare track ended externally → CLEARING local preview');
                this.#screenshare.video = false;
                this.#screenshare.videoMuted = false;
                if (this.#localScreenElement) {
                    this.#localScreenElement.srcObject = null;
                }
                this.onLocalMemberMediaChange?.();
                this.#fireMemberStateChange(this.#localMemberId);
                
            } else if (streamType === 'camera') {
                const sourceTrack = this.#localVideoElement?.srcObject?.getVideoTracks()[0];
                
                if (sourceTrack && sourceTrack.readyState === 'live') {
                    console.debug('📹 Camera broadcast stopped but track still live → KEEPING local preview');
                    return; // Don't clear local state
                }
                
                // Track actually ended (hardware disconnected, permissions revoked, etc.)
                console.debug('🔄 Camera track ended externally → CLEARING local preview');
                this.#localMedia.video = false;
                this.#localMedia.videoMuted = false;
                if (this.#localVideoElement) {
                    this.#localVideoElement.srcObject = null;
                }
                this.onLocalMemberMediaChange?.();
                this.#fireMemberStateChange(this.#localMemberId);
            }
            return; // Don't process as remote member
        }
        
        // Handle remote member stream end
        if (streamType === 'camera') {
            member.hasCamera = false;
            BrowserActivity.disconnectRemoteAudio(memberId);
        } else if (streamType === 'screenshare') {
            member.hasScreenshare = false;
            // Auto-cleanup registered screenshare tile
            this.#removeTile(memberId, 'screenshare');
        }
        BrowserActivity.setRemoteVideoCount(this.#getRemoteVideoCount());
        
        // Clear video element (use unified helper)
        this.#updateRemoteVideoVisibility(memberId, streamType);
        
        this.onRemoteMemberStreamEnd?.(memberId, streamType);
        if (streamType === 'camera') {
            this.onRemoteMemberLiveChange?.(memberId, false);
        }
        this.#fireMemberStateChange(memberId);
    }
    
    /**
     * Update remote video element visibility based on actual media state.
     * Keeps srcObject attached (for audio playback) regardless of video state.
     * Bridge disables video track when hidden/off - most browsers show black/nothing.
     * Apps can add overlays if needed for edge cases (frozen frames).
     * @private
     */
    #updateRemoteVideoVisibility(memberId, streamType) {
        const member = this.#members.get(memberId);
        if (!member) return;
        
        const videoEl = member.videoElements?.[streamType];
        if (!videoEl) return;
        
        // Get detailed media state from Bridge
        const states = this.#bridge?.getMemberMediaStates(memberId);
        const detailKey = streamType === 'camera' ? 'cam_video_detail' : 'screen_video_detail';
        const videoDetail = states?.[detailKey];
        
        // Only show video when it's actually ON (not MUTED, not OFF)
        const shouldShowVideo = videoDetail === 'ON';
        
        // Always ensure stream is attached (even when video hidden/off)
        // This keeps audio playing while video track is disabled by Bridge
        const stream = this.#bridge?.getMemberMediaStream(memberId, streamType);
        if (stream && videoEl.srcObject !== stream) {
            videoEl.srcObject = stream;
        }
        
        // Note: srcObject stays attached for audio playback
        // Disabled video tracks typically show black/nothing in modern browsers
        // Apps should add overlays if frozen frames appear in edge cases
    }
    
    /**
     * Handle async errors surfaced from lower layers (WebSocket disconnect, auth failure, etc.).
     * This is the single logging point for errors that cannot be caught by the developer's try/catch.
     * @private
     */
    #handleAsyncError(error) {
        console.error('[VibeLive]', error instanceof MakedoError ? error.toString() : error);
        this.onError?.('async', error);
    }

    #handleMemberMediaChange(memberId, streamType) {
        const member = this.#members.get(memberId);
        if (!member) return;
        
        // Update video element visibility based on actual state
        // This handles: mute→hide, unmute→show, video off with audio→hide
        this.#updateRemoteVideoVisibility(memberId, streamType);
        
        this.onRemoteMemberMediaChange?.(memberId, streamType);
        this.#fireMemberStateChange(memberId);
    }
    
    #clearMembers() {
        // Clear video elements for all members
        this.#members.forEach(member => {
            if (member.videoElements?.camera) {
                member.videoElements.camera.srcObject = null;
            }
            if (member.videoElements?.screenshare) {
                member.videoElements.screenshare.srcObject = null;
            }
        });
        
        this.#members.clear();
        this.#pendingMembers.clear();
    }
}
